import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import {
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  FilterAltOff as FilterAltOffIcon,
  MailOutline as MailOutlineIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  TouchApp as TouchAppIcon,
  TrendingUp as TrendingUpIcon,
  UploadFile as UploadFileIcon,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import html2canvas from 'html2canvas';
import {
  api,
  CpdAnalyticsResponse,
  CpdAnalyticsUsersQuery,
  CpdHistogramBucket,
  CpdHoursField,
  CpdIncomeBand,
  CpdPerformanceColumnFilters,
  CpdPerformanceResponse,
  CpdPerformanceSortBy,
  CpdPerformanceUserRow,
  CpdPerformanceUsersQuery,
  CpdRecipientEmail,
  CpdStatusBucket,
  CpdStatusCounts,
  CpdUserRow,
  isValidNumericFilter,
} from '../../services/api';
import * as XLSX from 'xlsx';
import { saveBlobDownload } from '../../../shared/utils/saveBlobDownload';

type TrainingCenter = 'Hastors' | 'Huttons' | 'Others';

const SPREADSHEET_ACCEPT =
  '.csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';

/** Recharts stack render order — first bar is the leftmost segment of the horizontal bands. */
const STACK_ORDER: CpdStatusBucket[] = ['Exempted', 'Passed', 'Ongoing'];

/** Status palette validated against the light card surface (#f2f5f9): deutan ΔE ≥ 8.6, contrast ≥ 3:1. */
const STATUS_COLORS_LIGHT: Record<CpdStatusBucket, string> = {
  Passed: '#059669',
  Exempted: '#64748B',
  Ongoing: '#DC2626',
};

/** Status palette validated against the dark card surface (#223046): deutan ΔE ≥ 10, contrast ≥ 3:1. */
const STATUS_COLORS_DARK: Record<CpdStatusBucket, string> = {
  Passed: '#57D9A3',
  Exempted: '#95A1B4',
  Ongoing: '#F0616E',
};

const STATUS_KEY: Record<CpdStatusBucket, keyof CpdStatusCounts> = {
  Passed: 'passed',
  Exempted: 'exempted',
  Ongoing: 'ongoing',
};

/** Single-hue sequential red ramp — more outstanding hours reads more intense on either surface. */
function colorForHours(hours: number, maxHours: number, isDark: boolean): string {
  const ratio = maxHours > 0 ? Math.min(1, Math.max(0, hours / maxHours)) : 0;
  if (isDark) {
    // Dark surface: pale salmon → vivid red (rising chroma keeps the high end most severe).
    const saturation = 52 + 34 * ratio;
    const lightness = 67 - 9 * ratio;
    return `hsl(2, ${saturation.toFixed(0)}%, ${lightness.toFixed(0)}%)`;
  }
  const lightness = 66 - 24 * ratio; // light surface: deeper = more hours
  return `hsl(2, 70%, ${lightness.toFixed(0)}%)`;
}

function hexToRgba(hex: string, alpha: number): string {
  const value = hex.replace('#', '');
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// ---- Spreadsheet parsing (CSV + XLSX) ------------------------------------

function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let field = '';
  let row: string[] = [];
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } else { inQuotes = false; }
      } else { field += ch; }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(field); field = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(field); field = '';
      if (row.some((c) => c.trim() !== '')) rows.push(row);
      row = [];
    } else { field += ch; }
  }
  if (field !== '' || row.length) { row.push(field); if (row.some((c) => c.trim() !== '')) rows.push(row); }
  return rows;
}

function cellToString(cell: unknown): string {
  if (cell == null || cell === '') return '';
  if (cell instanceof Date) return cell.toISOString();
  return String(cell).trim();
}

function isExcelFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return (
    name.endsWith('.xlsx') ||
    name.endsWith('.xls') ||
    file.type.includes('spreadsheet') ||
    file.type.includes('excel')
  );
}

/** Parse the first worksheet of a CSV or Excel upload into string rows. */
async function parseSpreadsheetRows(file: File): Promise<string[][]> {
  if (isExcelFile(file)) {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) throw new Error('Excel file has no worksheets');
    const sheet = workbook.Sheets[sheetName];
    const raw = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
      header: 1,
      raw: false,
      defval: '',
    });
    return raw
      .map((row) => (Array.isArray(row) ? row : []).map(cellToString))
      .filter((row) => row.some((c) => c.trim() !== ''));
  }
  return parseCsv(await file.text());
}

function normalizeHeader(h: string): string {
  return h.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Match full header names and truncated Excel column labels (e.g. "Agent CE" → "Agent CEA"). */
function headersMatch(headerNorm: string, candidateNorm: string): boolean {
  if (!headerNorm || !candidateNorm) return false;
  if (headerNorm === candidateNorm) return true;
  const shorter = headerNorm.length <= candidateNorm.length ? headerNorm : candidateNorm;
  const longer = headerNorm.length > candidateNorm.length ? headerNorm : candidateNorm;
  return longer.startsWith(shorter) && shorter.length >= 5;
}

/** Build a lookup from normalised header name → column index. */
function headerIndex(header: string[]): (candidates: string[]) => number {
  const normalized = header.map(normalizeHeader);
  return (candidates: string[]) => {
    for (const c of candidates) {
      const key = normalizeHeader(c);
      const exact = normalized.indexOf(key);
      if (exact !== -1) return exact;
    }
    for (const c of candidates) {
      const key = normalizeHeader(c);
      for (let i = 0; i < normalized.length; i++) {
        if (headersMatch(normalized[i], key)) return i;
      }
    }
    return -1;
  };
}

/**
 * Parse purchase timestamps from Hastor exports (DD/MM/YYYY h:mm AM/PM) and
 * ISO strings produced by Excel/CSV parsers.
 */
function parsePurchaseDateTime(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const sgDateTime = trimmed.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i,
  );
  if (sgDateTime) {
    const day = parseInt(sgDateTime[1], 10);
    const month = parseInt(sgDateTime[2], 10);
    const year = parseInt(sgDateTime[3], 10);
    let hour = parseInt(sgDateTime[4], 10);
    const minute = parseInt(sgDateTime[5], 10);
    const ampm = sgDateTime[7].toUpperCase();
    if (ampm === 'PM' && hour !== 12) hour += 12;
    if (ampm === 'AM' && hour === 12) hour = 0;
    const d = new Date(year, month - 1, day, hour, minute, 0, 0);
    if (!Number.isNaN(d.getTime())) return d.toISOString();
  }

  const sgDate = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (sgDate) {
    const d = new Date(
      parseInt(sgDate[3], 10),
      parseInt(sgDate[2], 10) - 1,
      parseInt(sgDate[1], 10),
    );
    if (!Number.isNaN(d.getTime())) return d.toISOString();
  }

  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) return parsed.toISOString();
  return null;
}

function toIntCsv(value: string | undefined): number {
  const n = parseInt(String(value ?? '').replace(/[^0-9-]/g, ''), 10);
  return Number.isFinite(n) ? n : 0;
}

/** Extract attended date from a filename containing CPD-yyyymmdd (other words allowed). */
function parseAttendedDateFromFilename(fileName: string): string | null {
  const match = fileName.match(/CPD-(\d{8})/i);
  if (!match) return null;
  const raw = match[1];
  const year = parseInt(raw.slice(0, 4), 10);
  const month = parseInt(raw.slice(4, 6), 10);
  const day = parseInt(raw.slice(6, 8), 10);
  if (!year || month < 1 || month > 12 || day < 1 || day > 31) return null;
  const d = new Date(year, month - 1, day);
  if (
    Number.isNaN(d.getTime()) ||
    d.getFullYear() !== year ||
    d.getMonth() !== month - 1 ||
    d.getDate() !== day
  ) {
    return null;
  }
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T00:00:00`;
}

/** Keep each upload request small to avoid HTTP / SQL timeouts on large files. */
const UPLOAD_BATCH_SIZE = 400;

async function uploadRowsInBatches<T>(
  rows: T[],
  upload: (
    batch: T[],
    options: { part: number; totalParts: number; replaceSnapshot: boolean },
  ) => Promise<{ imported: number; skipped: number; skipReasons?: Record<string, number> }>,
  onProgress?: (part: number, totalParts: number) => void,
): Promise<{ imported: number; skipped: number; skipReasons: Record<string, number> }> {
  if (rows.length === 0) return { imported: 0, skipped: 0, skipReasons: {} };
  const totalParts = Math.ceil(rows.length / UPLOAD_BATCH_SIZE);
  let imported = 0;
  let skipped = 0;
  const skipReasons: Record<string, number> = {};
  for (let part = 0; part < totalParts; part++) {
    onProgress?.(part + 1, totalParts);
    const batch = rows.slice(part * UPLOAD_BATCH_SIZE, (part + 1) * UPLOAD_BATCH_SIZE);
    const result = await upload(batch, {
      part,
      totalParts,
      replaceSnapshot: part === 0,
    });
    imported += result.imported;
    skipped += result.skipped;
    if (result.skipReasons) {
      for (const [reason, count] of Object.entries(result.skipReasons)) {
        skipReasons[reason] = (skipReasons[reason] || 0) + count;
      }
    }
  }
  return { imported, skipped, skipReasons };
}

// ---- Small presentational helpers -----------------------------------------

interface ChartTheme {
  isDark: boolean;
  grid: string;
  axis: string;
  tick: { fill: string; fontSize: number };
  status: Record<CpdStatusBucket, string>;
  /** Card surface behind the charts — doubles as the separator stroke between stacked segments. */
  surface: string;
  /** Ink that stays readable on top of the status fills (fills are light in dark mode, dark in light mode). */
  onStatusInk: string;
  accentBlue: string;
  hoverCursorFill: string;
}

function useChartTheme(): ChartTheme {
  const muiTheme = useMuiTheme();
  const isDark = muiTheme.palette.mode === 'dark';
  return useMemo(
    () => ({
      isDark,
      grid: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(31,41,68,0.10)',
      axis: isDark ? 'rgba(255,255,255,0.28)' : 'rgba(31,41,68,0.24)',
      tick: { fill: muiTheme.palette.text.secondary, fontSize: 12 },
      status: isDark ? STATUS_COLORS_DARK : STATUS_COLORS_LIGHT,
      surface: isDark ? '#223046' : '#f2f5f9',
      onStatusInk: isDark ? '#16233C' : '#FFFFFF',
      accentBlue: isDark ? '#7EA6FF' : '#3056D3',
      hoverCursorFill: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(31,41,68,0.05)',
    }),
    [isDark, muiTheme.palette.text.secondary],
  );
}

// ---- Shared chart tooltip ---------------------------------------------------

interface RechartsTooltipItem {
  name?: string | number;
  value?: string | number;
  color?: string;
  dataKey?: string | number;
  payload?: unknown;
}

interface RechartsTooltipContentProps {
  active?: boolean;
  label?: string | number;
  payload?: RechartsTooltipItem[];
}

interface ChartTooltipRow {
  color?: string;
  name: React.ReactNode;
  value: React.ReactNode;
}

const ChartTooltipBox: React.FC<{
  isDark: boolean;
  title?: React.ReactNode;
  rows: ChartTooltipRow[];
  footer?: React.ReactNode;
}> = ({ isDark, title, rows, footer }) => (
  <Box
    sx={{
      backgroundColor: isDark ? '#2B3A54' : '#ffffff',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.16)' : 'rgba(31,41,68,0.14)'}`,
      borderRadius: 1.5,
      boxShadow: '0 8px 24px rgba(0,0,0,0.28)',
      px: 1.5,
      py: 1,
      fontSize: 12,
      color: isDark ? 'rgba(232,237,246,0.92)' : 'rgba(31,41,68,0.92)',
      pointerEvents: 'none',
    }}
  >
    {title !== undefined && (
      <Box sx={{ fontWeight: 700, mb: rows.length ? 0.5 : 0, color: isDark ? '#EDF2FA' : '#1F2944' }}>
        {title}
      </Box>
    )}
    {rows.map((row, idx) => (
      <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.15 }}>
        {row.color && (
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: row.color, flexShrink: 0 }} />
        )}
        <Box sx={{ opacity: 0.8 }}>{row.name}</Box>
        <Box sx={{ ml: 'auto', fontWeight: 700, fontVariantNumeric: 'tabular-nums', pl: 1.5 }}>
          {row.value}
        </Box>
      </Box>
    ))}
    {footer && <Box sx={{ mt: 0.5, opacity: 0.6, fontSize: 11 }}>{footer}</Box>}
  </Box>
);

/** Deterministic legend — recharts v3 orders store-driven legends unpredictably across stacks. */
const ChartLegendContent: React.FC<{ items: Array<{ label: string; color: string }> }> = ({ items }) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2, pt: 0.75 }}>
    {items.map((item) => (
      <Box
        key={item.label}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.6,
          fontSize: 12,
          fontWeight: 600,
          color: 'rgba(var(--color-text-rgb), 0.72)',
        }}
      >
        <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: item.color, flexShrink: 0 }} />
        {item.label}
      </Box>
    ))}
  </Box>
);

export const cardSx = {
  p: { xs: 2, md: 2.5 },
  bgcolor: 'var(--color-surface-elevated)',
  border: '1px solid rgba(var(--color-border-rgb), 0.16)',
  borderRadius: 2.5,
  height: '100%',
} as const;

export const sectionTitleSx = {
  fontWeight: 700,
  color: 'var(--color-text-strong)',
} as const;

/** Quiet card-header action ("Download Image", "Export CSV") sitting beside the title. */
const cardActionSx = {
  flexShrink: 0,
  textTransform: 'none',
  fontWeight: 600,
  color: 'rgba(var(--color-text-rgb), 0.72)',
  minWidth: 0,
  px: 1,
} as const;

const chartHintSx = {
  color: 'rgba(var(--color-text-rgb), 0.55)',
  display: 'block',
  mt: 1,
  textAlign: 'center',
} as const;

async function downloadElementAsPng(el: HTMLElement, fileName: string): Promise<void> {
  const computedBg = getComputedStyle(el).backgroundColor;
  const backgroundColor =
    !computedBg || computedBg === 'transparent' || computedBg === 'rgba(0, 0, 0, 0)'
      ? '#ffffff'
      : computedBg;

  // 2× keeps chart text/bars sharp; toBlob (below) still avoids slow base64 encoding.
  const canvas = await html2canvas(el, {
    useCORS: true,
    scale: 2,
    backgroundColor,
    logging: false,
    imageTimeout: 0,
    ignoreElements: (node) =>
      node instanceof HTMLElement && node.dataset.html2canvasIgnore === 'true',
  });

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/png');
  });
  if (!blob) throw new Error('Failed to encode PNG');

  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const datedFileName = `${fileName}-${yyyy}-${mm}-${dd}.png`;

  // Via saveBlobDownload so native WebViews keep the name and .png extension
  // instead of saving a nameless .bin blob.
  await saveBlobDownload(blob, datedFileName, 'image/png');
}

/**
 * "Download Image" action for a card. Carries the html2canvas ignore marker so
 * the button never shows up inside its own capture.
 */
const CpdDownloadImageButton: React.FC<{
  targetRef: React.RefObject<HTMLElement | null>;
  fileName: string;
}> = ({ targetRef, fileName }) => {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = useCallback(async () => {
    if (!targetRef.current || downloading) return;
    setDownloading(true);
    try {
      await downloadElementAsPng(targetRef.current, fileName);
    } catch (err) {
      console.error('Failed to download CPD section PNG:', err);
    } finally {
      setDownloading(false);
    }
  }, [downloading, fileName, targetRef]);

  return (
    <Button
      data-html2canvas-ignore="true"
      size="small"
      variant="text"
      onClick={handleDownload}
      disabled={downloading}
      startIcon={downloading ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
      sx={cardActionSx}
    >
      Download Image
    </Button>
  );
};

/** Chart section with accent title and optional subtitle. */
export const CpdSectionCard: React.FC<{
  title: string;
  /** File name for a PNG download of the whole card. Omit to drop the button. */
  fileName?: string;
  subtitle?: string;
  children: React.ReactNode;
}> = ({ title, fileName, subtitle, children }) => {
  const ref = useRef<HTMLDivElement | null>(null);

  return (
    <Paper ref={ref} elevation={0} sx={cardSx}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.25, minWidth: 0 }}>
          <Box
            sx={{
              width: 4,
              alignSelf: 'stretch',
              borderRadius: 99,
              bgcolor: 'var(--color-accent)',
              flexShrink: 0,
              my: 0.25,
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ ...sectionTitleSx, lineHeight: 1.3 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', display: 'block' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
        {fileName && <CpdDownloadImageButton targetRef={ref} fileName={fileName} />}
      </Box>
      {children}
    </Paper>
  );
};

function formatBarLabel(value: unknown): string {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n <= 0) return '';
  return String(n);
}

/** Temporary: use mock last-week + this-week data so charts/tables can be reviewed without backend snapshots. */
const USE_CPD_MOCK_DATA = false;

function buildMockUsers(): CpdUserRow[] {
  const mockUsers: CpdUserRow[] = [];
  const pushUsers = (
    count: number,
    status: CpdStatusBucket,
    center: 'hastors' | 'huttons' | 'others',
    hoursBase: number,
  ) => {
    for (let i = 0; i < count; i++) {
      const totalOutstanding = status === 'Ongoing' ? Math.max(1, (hoursBase + (i % 8)) % 17 || 1) : 0;
      const pe = Math.floor(totalOutstanding / 2);
      const sl = Math.floor((totalOutstanding - pe) / 2);
      const sdl = totalOutstanding - pe - sl;
      mockUsers.push({
        ceaNumber: `R${String(mockUsers.length + 100000).padStart(6, '0')}A`,
        name: `Agent ${mockUsers.length + 1}`,
        mobile: `9${String(1000000 + mockUsers.length).slice(0, 7)}`,
        email: `agent${mockUsers.length + 1}@example.com`,
        division: mockUsers.length % 5 === 0 ? null : `Division ${(mockUsers.length % 4) + 1}`,
        cpdCycle: '2026',
        status,
        outstandingPE: pe,
        outstandingSL: sl,
        outstandingSDL: sdl,
        totalOutstanding,
        hastors: center === 'hastors',
        huttons: center === 'huttons',
        others: center === 'others',
      });
    }
  };

  pushUsers(420, 'Ongoing', 'hastors', 4);
  pushUsers(310, 'Passed', 'hastors', 0);
  pushUsers(40, 'Exempted', 'hastors', 0);
  pushUsers(680, 'Ongoing', 'huttons', 6);
  pushUsers(920, 'Passed', 'huttons', 0);
  pushUsers(55, 'Exempted', 'huttons', 0);
  pushUsers(1100, 'Ongoing', 'others', 3);
  pushUsers(780, 'Passed', 'others', 0);
  pushUsers(70, 'Exempted', 'others', 0);
  return mockUsers;
}

function buildMockHistogram(users: CpdUserRow[], field: CpdHoursField = 'total'): CpdHistogramBucket[] {
  const value = (u: CpdUserRow) => {
    if (field === 'pe') return u.outstandingPE;
    if (field === 'sl') return u.outstandingSL;
    if (field === 'sdl') return u.outstandingSDL;
    return u.totalOutstanding;
  };
  const counts = new Map<number, number>();
  for (const u of users) {
    const hours = Math.round(value(u));
    if (hours <= 0) continue;
    counts.set(hours, (counts.get(hours) || 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([hours, count]) => ({ hours, count }))
    .sort((a, b) => b.hours - a.hours);
}

function buildMockCpdAnalytics(): CpdAnalyticsResponse & { users: CpdUserRow[] } {
  const mockUsers = buildMockUsers();
  return {
    current: {
      statusCounts: { ongoing: 2200, passed: 2010, exempted: 165 },
      lastUploadedAt: new Date().toISOString(),
    },
    lastWeek: {
      statusCounts: { ongoing: 2450, passed: 1780, exempted: 150 },
      hasSnapshot: true,
    },
    trainingCenters: {
      hastors: { ongoing: 420, passed: 310, exempted: 40 },
      huttons: { ongoing: 680, passed: 920, exempted: 55 },
      others: { ongoing: 1100, passed: 780, exempted: 70 },
    },
    lastWeekTrainingCenters: {
      hastors: { ongoing: 480, passed: 260, exempted: 35 },
      huttons: { ongoing: 620, passed: 850, exempted: 50 },
      others: { ongoing: 1350, passed: 670, exempted: 65 },
    },
    divisions: (() => {
      const byDiv = new Map<string, CpdStatusCounts>();
      for (const u of mockUsers) {
        const division = u.division || '-';
        let counts = byDiv.get(division);
        if (!counts) {
          counts = { passed: 0, exempted: 0, ongoing: 0 };
          byDiv.set(division, counts);
        }
        if (u.status === 'Passed') counts.passed += 1;
        else if (u.status === 'Exempted') counts.exempted += 1;
        else counts.ongoing += 1;
      }
      return Array.from(byDiv.entries())
        .map(([division, counts]) => ({ division, ...counts }))
        .sort((a, b) => (b.passed + b.ongoing) - (a.passed + a.ongoing));
    })(),
    histogram: buildMockHistogram(mockUsers, 'total'),
    histogramPE: buildMockHistogram(mockUsers, 'pe'),
    histogramSL: buildMockHistogram(mockUsers, 'sl'),
    histogramSDL: buildMockHistogram(mockUsers, 'sdl'),
    users: mockUsers,
  };
}

// ---- Histogram (chart 3 + drill-down reuse) --------------------------------

type HistogramDatum = CpdHistogramBucket;

const CpdHistogram: React.FC<{
  data: HistogramDatum[];
  onBarClick: (hours: number) => void;
  height?: number;
  emptyMessage?: string;
  xAxisLabel?: string;
}> = ({ data, onBarClick, height = 260, emptyMessage = 'No data available.', xAxisLabel = 'Outstanding hours' }) => {
  const theme = useChartTheme();
  const maxHours = data.length ? Math.max(...data.map((d) => d.hours)) : 0;

  const renderTooltip = useCallback(
    (props: unknown) => {
      const { active, payload, label } = props as RechartsTooltipContentProps;
      if (!active || !payload?.length) return null;
      const count = Number(payload[0]?.value ?? 0);
      return (
        <ChartTooltipBox
          isDark={theme.isDark}
          title={`${label} outstanding hour${Number(label) === 1 ? '' : 's'}`}
          rows={[{ name: 'Agents', value: count.toLocaleString() }]}
          footer="Click the bar to list these agents"
        />
      );
    },
    [theme.isDark],
  );

  if (data.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', py: 4, textAlign: 'center' }}>
        {emptyMessage}
      </Typography>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 22, right: 12, left: 4, bottom: 24 }} barCategoryGap="18%">
        <CartesianGrid stroke={theme.grid} strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="hours"
          stroke={theme.axis}
          tick={theme.tick}
          tickLine={false}
          interval={0}
          label={{
            value: xAxisLabel,
            position: 'insideBottom',
            offset: -12,
            fill: theme.tick.fill,
            fontSize: 12,
          }}
        />
        <YAxis
          stroke={theme.axis}
          tick={false}
          axisLine={false}
          width={28}
          label={{
            value: 'Agents',
            angle: -90,
            position: 'insideLeft',
            fill: theme.tick.fill,
            fontSize: 12,
          }}
        />
        <RechartsTooltip cursor={{ fill: theme.hoverCursorFill }} content={renderTooltip} />
        <Bar
          dataKey="count"
          radius={[4, 4, 0, 0]}
          cursor="pointer"
          onClick={(e: { payload?: HistogramDatum }) => {
            if (e?.payload) onBarClick(e.payload.hours);
          }}
        >
          {data.map((d) => (
            <Cell key={d.hours} fill={colorForHours(d.hours, maxHours, theme.isDark)} />
          ))}
          <LabelList
            dataKey="count"
            position="top"
            formatter={formatBarLabel}
            style={{ fill: theme.tick.fill, fontSize: 10, fontWeight: 600 }}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

type OutstandingCategoryField = 'pe' | 'sl' | 'sdl';

/** Single bar: agents with any outstanding PE hours (typically 4h). */
const CpdPeOutstandingBar: React.FC<{
  pe: HistogramDatum[];
  onBarClick: (hours: number) => void;
  height?: number;
}> = ({ pe, onBarClick, height = 260 }) => {
  const theme = useChartTheme();
  const outstandingTotal = pe.reduce((s, d) => s + (d.count > 0 ? d.count : 0), 0);
  // PE outstanding is almost always a single bucket (4h); use the largest bucket for drill-down.
  const primaryHours = pe.reduce<HistogramDatum | null>((best, d) => {
    if (d.count <= 0) return best;
    if (!best || d.count > best.count) return d;
    return best;
  }, null)?.hours ?? 4;

  const data = [{ name: 'Outstanding PE hours', count: outstandingTotal }];

  const renderTooltip = useCallback(
    (props: unknown) => {
      const { active, payload } = props as RechartsTooltipContentProps;
      if (!active || !payload?.length) return null;
      const count = Number(payload[0]?.value ?? 0);
      return (
        <ChartTooltipBox
          isDark={theme.isDark}
          title="Outstanding PE"
          rows={[{ name: 'Agents', value: count.toLocaleString() }]}
          footer="Click the bar to list these agents"
        />
      );
    },
    [theme.isDark],
  );

  if (outstandingTotal <= 0) {
    return (
      <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', py: 4, textAlign: 'center' }}>
        No agents with outstanding PE.
      </Typography>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 28, right: 12, left: 4, bottom: 8 }}>
        <CartesianGrid stroke={theme.grid} strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="name" stroke={theme.axis} tick={theme.tick} tickLine={false} />
        <YAxis
          allowDecimals={false}
          stroke={theme.axis}
          tick={theme.tick}
          width={40}
          label={{
            value: 'Agents',
            angle: -90,
            position: 'insideLeft',
            fill: theme.tick.fill,
            fontSize: 12,
          }}
        />
        <RechartsTooltip cursor={{ fill: theme.hoverCursorFill }} content={renderTooltip} />
        <Bar
          dataKey="count"
          fill={theme.status.Ongoing}
          radius={[4, 4, 0, 0]}
          maxBarSize={72}
          cursor="pointer"
          onClick={() => onBarClick(primaryHours)}
        >
          <LabelList
            dataKey="count"
            position="top"
            style={{ fill: theme.tick.fill, fontSize: 12, fontWeight: 700 }}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

/** PE outstanding bar + SL / SDL histograms in one downloadable section. */
const CpdOutstandingBreakdownSection: React.FC<{
  pe: HistogramDatum[];
  sl: HistogramDatum[];
  sdl: HistogramDatum[];
  onBarClick: (hours: number, field: OutstandingCategoryField) => void;
}> = ({ pe, sl, sdl, onBarClick }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: { xs: 'column', md: 'row' },
      flexWrap: 'wrap',
      gap: 2,
      alignItems: 'stretch',
    }}
  >
    <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'var(--color-text-strong)', display: 'block', mb: 0.5 }}>
        PE
      </Typography>
      <CpdPeOutstandingBar
        pe={pe}
        onBarClick={(hours) => onBarClick(hours, 'pe')}
        height={260}
      />
    </Box>
    <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'var(--color-text-strong)', display: 'block', mb: 0.5 }}>
        SL
      </Typography>
      <CpdHistogram
        data={sl}
        onBarClick={(hours) => onBarClick(hours, 'sl')}
        height={260}
        xAxisLabel="Outstanding SL hours"
      />
    </Box>
    <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'var(--color-text-strong)', display: 'block', mb: 0.5 }}>
        SDL
      </Typography>
      <CpdHistogram
        data={sdl}
        onBarClick={(hours) => onBarClick(hours, 'sdl')}
        height={260}
        xAxisLabel="Outstanding SDL hours"
      />
    </Box>
  </Box>
);

// ---- User list dialog (lazy-loaded from API) -------------------------------

const USER_LIST_PAGE_SIZE = 25;

export type UserListFilter = Pick<
  CpdAnalyticsUsersQuery,
  | 'status'
  | 'center'
  | 'division'
  | 'hours'
  | 'snapshot'
  | 'hoursField'
  | 'anyOutstanding'
  | 'includeAll'
  | 'team'
>;

function matchesDivisionFilter(u: CpdUserRow, division: string | undefined): boolean {
  if (!division) return true;
  return (u.division || '-') === division;
}

function filterHasUserListScope(filter: UserListFilter | null | undefined): boolean {
  if (!filter) return false;
  if (filter.includeAll) return true;
  if (filter.anyOutstanding) return true;
  return filter.hours !== undefined && filter.hours !== null;
}
type UserListSortBy = 'total' | 'pe' | 'sl' | 'sdl';
type UserListSortDir = 'asc' | 'desc';

const HOURS_FIELD_LABEL: Record<CpdHoursField, string> = {
  total: 'Total',
  pe: 'PE',
  sl: 'SL',
  sdl: 'SDL',
};

function hoursValue(u: CpdUserRow, field: CpdHoursField): number {
  if (field === 'pe') return u.outstandingPE;
  if (field === 'sl') return u.outstandingSL;
  if (field === 'sdl') return u.outstandingSDL;
  return u.totalOutstanding;
}

const CpdSendOutstandingEmailDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  contextLabel: string;
  attachmentFileNameHint?: string;
  buildAttachment: () => Promise<{ base64: string; fileName: string; agentCount: number }>;
}> = ({ open, onClose, contextLabel, attachmentFileNameHint, buildAttachment }) => {
  const [recipients, setRecipients] = useState<CpdRecipientEmail[]>([]);
  const [ccRecipients, setCcRecipients] = useState<CpdRecipientEmail[]>([]);
  const [recipientOptions, setRecipientOptions] = useState<CpdRecipientEmail[]>([]);
  const [ccOptions, setCcOptions] = useState<CpdRecipientEmail[]>([]);
  const [recipientInput, setRecipientInput] = useState('');
  const [ccInput, setCcInput] = useState('');
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [loadingCc, setLoadingCc] = useState(false);
  const [subject, setSubject] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const attachmentChipLabel = useMemo(() => {
    if (attachmentFileNameHint) return attachmentFileNameHint;
    const safe = (contextLabel || 'cpd-agents').replace(/[^\w\-]+/g, '_').slice(0, 40);
    const now = new Date();
    const stamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    return `${safe}-${stamp}.xlsx`;
  }, [attachmentFileNameHint, contextLabel]);

  const selectedToKeys = useMemo(
    () => new Set(recipients.map((r) => r.email.toLowerCase())),
    [recipients],
  );
  const selectedCcKeys = useMemo(
    () => new Set(ccRecipients.map((r) => r.email.toLowerCase())),
    [ccRecipients],
  );

  useEffect(() => {
    if (!open) {
      setRecipients([]);
      setCcRecipients([]);
      setRecipientOptions([]);
      setCcOptions([]);
      setRecipientInput('');
      setCcInput('');
      setSubject('');
      setMessageBody('');
      setError(null);
      setSuccess(null);
      setLoadingTemplate(false);
      setSavingTemplate(false);
      setSending(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingTemplate(true);
      setError(null);
      try {
        const tpl = await api.getCpdEmailTemplate();
        if (!cancelled) {
          setSubject(tpl.subject);
          setMessageBody(tpl.messageBody);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load email template');
        }
      } finally {
        if (!cancelled) setLoadingTemplate(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const q = recipientInput.trim();
    if (q.length < 2) {
      setRecipientOptions([]);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setLoadingRecipients(true);
      try {
        const rows = await api.searchCpdRecipientEmails(q);
        if (!cancelled) {
          setRecipientOptions(rows.filter((r) => !selectedCcKeys.has(r.email.toLowerCase())));
        }
      } catch {
        if (!cancelled) setRecipientOptions([]);
      } finally {
        if (!cancelled) setLoadingRecipients(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, recipientInput, selectedCcKeys]);

  useEffect(() => {
    if (!open) return;
    const q = ccInput.trim();
    if (q.length < 2) {
      setCcOptions([]);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setLoadingCc(true);
      try {
        const rows = await api.searchCpdRecipientEmails(q);
        if (!cancelled) {
          setCcOptions(rows.filter((r) => !selectedToKeys.has(r.email.toLowerCase())));
        }
      } catch {
        if (!cancelled) setCcOptions([]);
      } finally {
        if (!cancelled) setLoadingCc(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, ccInput, selectedToKeys]);

  const handleSaveTemplate = useCallback(async () => {
    if (savingTemplate) return;
    setSavingTemplate(true);
    setError(null);
    setSuccess(null);
    try {
      const saved = await api.saveCpdEmailTemplate({ subject, messageBody });
      setSubject(saved.subject);
      setMessageBody(saved.messageBody);
      setSuccess('Template saved. Future emails will open with this subject and message.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  }, [savingTemplate, subject, messageBody]);

  const handleSend = useCallback(async () => {
    if (sending) return;
    if (recipients.length === 0) {
      setError('Select at least one recipient.');
      return;
    }
    if (!subject.trim() || !messageBody.trim()) {
      setError('Subject and message body are required.');
      return;
    }
    setSending(true);
    setError(null);
    setSuccess(null);
    try {
      const attachment = await buildAttachment();
      if (attachment.agentCount === 0) {
        throw new Error('No agents to attach for this list.');
      }
      const result = await api.sendCpdOutstandingReminder({
        recipients: recipients.map((r) => r.email),
        cc: ccRecipients.map((r) => r.email),
        subject: subject.trim(),
        messageBody: messageBody.trim(),
        attachmentBase64: attachment.base64,
        attachmentFileName: attachment.fileName,
      });
      const parts = [
        result.sent > 0 ? `sent to ${result.sent}` : null,
        result.skipped > 0 ? `skipped ${result.skipped} (non-prod)` : null,
        result.failed > 0 ? `failed ${result.failed}` : null,
      ].filter(Boolean);
      setSuccess(
        parts.length
          ? `Email ${parts.join(', ')}. Excel of ${attachment.agentCount} agent${attachment.agentCount === 1 ? '' : 's'} attached.`
          : 'Email request completed.',
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send email');
    } finally {
      setSending(false);
    }
  }, [sending, recipients, ccRecipients, subject, messageBody, buildAttachment]);

  return (
    <Dialog
      open={open}
      onClose={sending || savingTemplate ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)' } }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography variant="h6">Send outstanding-hours email</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.72)' }}>
              {contextLabel}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" disabled={sending || savingTemplate}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2.5 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          You can save the subject and message as a template for future outstanding-hours emails.
          The current agent list will be attached as an Excel file when you send.
        </Alert>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
        {loadingTemplate ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={2}>
            <Autocomplete
              multiple
              options={recipientOptions}
              value={recipients}
              loading={loadingRecipients}
              filterOptions={(x) => x}
              getOptionLabel={(opt) =>
                opt.name ? `${opt.email} (${opt.name})` : opt.email
              }
              isOptionEqualToValue={(a, b) => a.email.toLowerCase() === b.email.toLowerCase()}
              onChange={(_, next) => {
                setRecipients(next);
                setCcRecipients((prev) =>
                  prev.filter((c) => !next.some((t) => t.email.toLowerCase() === c.email.toLowerCase())),
                );
              }}
              onInputChange={(_, value, reason) => {
                if (reason === 'input' || reason === 'clear') setRecipientInput(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Recipients"
                  placeholder="Type to search active user emails…"
                  helperText="Only Active users are listed. Select one or more recipients."
                />
              )}
            />
            <Autocomplete
              multiple
              options={ccOptions}
              value={ccRecipients}
              loading={loadingCc}
              filterOptions={(x) => x}
              getOptionLabel={(opt) =>
                opt.name ? `${opt.email} (${opt.name})` : opt.email
              }
              isOptionEqualToValue={(a, b) => a.email.toLowerCase() === b.email.toLowerCase()}
              onChange={(_, next) => setCcRecipients(next)}
              onInputChange={(_, value, reason) => {
                if (reason === 'input' || reason === 'clear') setCcInput(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cc"
                  placeholder="Type to search active user emails…"
                  helperText="Optional. Addresses already in Recipients are excluded."
                />
              )}
            />
            <TextField
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              fullWidth
              inputProps={{ maxLength: 250 }}
            />
            <TextField
              label="Message body"
              value={messageBody}
              onChange={(e) => setMessageBody(e.target.value)}
              fullWidth
              multiline
              minRows={8}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                icon={<AttachFileIcon />}
                label={attachmentChipLabel}
                size="small"
                variant="outlined"
                sx={{ maxWidth: '100%' }}
              />
              <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>
                Agent list Excel will be attached on send
              </Typography>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1, borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Button
          onClick={handleSaveTemplate}
          disabled={loadingTemplate || savingTemplate || sending || !subject.trim() || !messageBody.trim()}
          sx={{ textTransform: 'none' }}
        >
          {savingTemplate ? 'Saving…' : 'Save as template'}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={sending || savingTemplate} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={loadingTemplate || sending || savingTemplate || recipients.length === 0}
          startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <MailOutlineIcon />}
          sx={{ textTransform: 'none' }}
        >
          {sending ? 'Sending…' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Long list = every agent in the segment. Short list = only the ones at or above
 * the performer benchmark in commission, which is the same set the Performance
 * tab calls a performer — so both views agree on who counts as producing.
 */
type AgentListMode = 'long' | 'short';

const CpdUserListDialog: React.FC<{
  open: boolean;
  title: string;
  subtitle?: string;
  filter: UserListFilter | null;
  onClose: () => void;
  /** Render list chrome only (no Dialog) for embedding in the histogram drill-down tabs. */
  embedded?: boolean;
  /** Show Send Email beside export (division Ongoing Agents tab). */
  allowSendEmail?: boolean;
  /**
   * Opens the commission drill-down for this same set of agents. The parent owns
   * the dialog so every outstanding-hours list can reach it without nesting.
   */
  onOpenPerformance?: () => void;
}> = ({
  open,
  title,
  subtitle,
  filter,
  onClose,
  embedded = false,
  allowSendEmail = false,
  onOpenPerformance,
}) => {
  const hoursField: CpdHoursField = filter?.hoursField ?? 'total';
  const defaultSortBy: UserListSortBy = hoursField === 'total' ? 'total' : hoursField;
  const [listMode, setListMode] = useState<AgentListMode>('long');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState<UserListSortBy>(defaultSortBy);
  const [sortDir, setSortDir] = useState<UserListSortDir>('desc');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<CpdUserRow[]>([]);
  const [total, setTotal] = useState(0);

  // Commission window behind the short list — the same year-to-date range the
  // Performance tab opens on, so the two never disagree on who is a performer.
  const shortListRange = useMemo(() => defaultPerformanceRange(), []);

  const hourColumns = useMemo(() => {
    if (hoursField === 'pe') return [{ key: 'pe' as const, label: 'Outstanding PE' }];
    if (hoursField === 'sl') return [{ key: 'sl' as const, label: 'Outstanding SL' }];
    if (hoursField === 'sdl') return [{ key: 'sdl' as const, label: 'Outstanding SDL' }];
    return [
      { key: 'total' as const, label: 'Outstanding Total', sortable: false },
      { key: 'pe' as const, label: 'Outstanding PE' },
      { key: 'sl' as const, label: 'Outstanding SL' },
      { key: 'sdl' as const, label: 'Outstanding SDL' },
    ];
  }, [hoursField]);

  const colSpan = 6 + hourColumns.length;
  const [exporting, setExporting] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setListMode('long');
      setSearchQuery('');
      setDebouncedSearch('');
      setPage(0);
      setSortBy(defaultSortBy);
      setSortDir('desc');
      setUsers([]);
      setTotal(0);
      setError(null);
      setExporting(false);
      setEmailDialogOpen(false);
    }
  }, [open, defaultSortBy]);

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => setDebouncedSearch(searchQuery.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [open, searchQuery]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, filter, sortBy, sortDir, listMode]);

  const handleSort = useCallback((column: UserListSortBy) => {
    setSortBy((prev) => {
      if (prev === column) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir('desc');
      return column;
    });
  }, []);

  useEffect(() => {
    if (!open || !filterHasUserListScope(filter)) return;
    const status = filter!.status;
    const center = filter!.center;
    const division = filter!.division;
    const hours = filter!.hours;
    const includeAll = !!filter!.includeAll;
    const anyOutstanding = !!filter!.anyOutstanding;
    const snapshot = filter!.snapshot;
    const team = filter!.team;
    const field = filter!.hoursField ?? 'total';
    const hasExactHours = hours !== undefined && hours !== null;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        if (listMode === 'short') {
          // Same segment scope, scored against commission. /performance/users
          // already loads GCE per agent, so there is no second income filter to
          // maintain on /analytics/users, and its rows extend CpdUserRow — the
          // table below renders them unchanged.
          const res = await api.getCpdPerformanceUsers({
            status,
            center,
            division,
            includeAll: !hasExactHours && includeAll ? true : undefined,
            hours: hasExactHours ? hours : undefined,
            hoursField: field,
            snapshot,
            team,
            startDate: shortListRange.startMonth,
            endDate: shortListRange.endMonth,
            benchmark: DEFAULT_PERFORMER_BENCHMARK,
            group: 'performer',
            search: debouncedSearch || undefined,
            page,
            pageSize: USER_LIST_PAGE_SIZE,
            sortBy,
            sortDir,
          });
          if (!cancelled) {
            setUsers(res.users);
            setTotal(res.total);
          }
          return;
        }
        if (USE_CPD_MOCK_DATA) {
          const mock = buildMockCpdAnalytics();
          let rows = mock.users;
          if (status) rows = rows.filter((u) => u.status === status);
          if (center) rows = rows.filter((u) => u[center]);
          if (division) rows = rows.filter((u) => matchesDivisionFilter(u, division));
          if (includeAll) {
            // keep all matched rows
          } else if (hasExactHours) {
            rows = rows.filter((u) => Math.round(hoursValue(u, field)) === hours);
          } else if (anyOutstanding) {
            rows = rows.filter((u) => hoursValue(u, field) > 0);
          }
          const q = debouncedSearch.toLowerCase();
          if (q) {
            rows = rows.filter(
              (u) =>
                (u.ceaNumber || '').toLowerCase().includes(q) ||
                (u.name || '').toLowerCase().includes(q) ||
                (u.email || '').toLowerCase().includes(q) ||
                (u.division || '').toLowerCase().includes(q),
            );
          }
          const dir = sortDir === 'asc' ? 1 : -1;
          rows = [...rows].sort((a, b) => {
            const va = hoursValue(a, sortBy);
            const vb = hoursValue(b, sortBy);
            const d = (va - vb) * dir;
            if (d !== 0) return d;
            return b.totalOutstanding - a.totalOutstanding;
          });
          if (!cancelled) {
            setTotal(rows.length);
            setUsers(rows.slice(page * USER_LIST_PAGE_SIZE, (page + 1) * USER_LIST_PAGE_SIZE));
          }
          return;
        }
        const res = await api.getCpdAnalyticsUsers({
          status,
          center,
          division,
          includeAll: !hasExactHours && includeAll ? true : undefined,
          hours: hasExactHours ? hours : undefined,
          anyOutstanding: !hasExactHours && anyOutstanding ? true : undefined,
          hoursField: field,
          snapshot,
          team,
          search: debouncedSearch || undefined,
          page,
          pageSize: USER_LIST_PAGE_SIZE,
          sortBy,
          sortDir,
        });
        if (!cancelled) {
          setUsers(res.users);
          setTotal(res.total);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load agents');
          setUsers([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, filter?.status, filter?.center, filter?.division, filter?.hours, filter?.includeAll, filter?.anyOutstanding, filter?.hoursField, filter?.snapshot, debouncedSearch, page, sortBy, sortDir, listMode, shortListRange]);

  const exportNameSuffix = listMode === 'short' ? '-short-list' : '';

  const buildExportRows = useCallback((rows: CpdUserRow[]) => {
    return rows.map((u) => {
      const base: Record<string, string | number> = {
        CEA: u.ceaNumber || '',
        Name: u.name || '',
        Status: u.status || '',
        Division: u.division || '-',
        Mobile: u.mobile || '',
        Email: u.email || '',
      };
      if (hoursField === 'pe') {
        base['Outstanding PE'] = u.outstandingPE;
      } else if (hoursField === 'sl') {
        base['Outstanding SL'] = u.outstandingSL;
      } else if (hoursField === 'sdl') {
        base['Outstanding SDL'] = u.outstandingSDL;
      } else {
        base['Outstanding Total'] = u.totalOutstanding;
        base['Outstanding PE'] = u.outstandingPE;
        base['Outstanding SL'] = u.outstandingSL;
        base['Outstanding SDL'] = u.outstandingSDL;
      }
      return base;
    });
  }, [hoursField]);

  const loadMatchingAgentRows = useCallback(async (): Promise<CpdUserRow[]> => {
    if (!filterHasUserListScope(filter)) return [];
    const field = filter!.hoursField ?? 'total';
    const hasExactHours = filter!.hours !== undefined && filter!.hours !== null;
    const includeAll = !!filter!.includeAll;
    const anyOutstanding = !!filter!.anyOutstanding;
    if (listMode === 'short') {
      const res = await api.getCpdPerformanceUsers({
        status: filter!.status,
        center: filter!.center,
        division: filter!.division,
        includeAll: !hasExactHours && includeAll ? true : undefined,
        hours: hasExactHours ? filter!.hours : undefined,
        hoursField: field,
        snapshot: filter!.snapshot,
        startDate: shortListRange.startMonth,
        endDate: shortListRange.endMonth,
        benchmark: DEFAULT_PERFORMER_BENCHMARK,
        group: 'performer',
        search: debouncedSearch || undefined,
        sortBy,
        sortDir,
        exportAll: true,
      });
      return res.users;
    }
    if (USE_CPD_MOCK_DATA) {
      const mock = buildMockCpdAnalytics();
      let matched = mock.users;
      if (filter!.status) matched = matched.filter((u) => u.status === filter!.status);
      if (filter!.center) matched = matched.filter((u) => u[filter!.center!]);
      if (filter!.division) matched = matched.filter((u) => matchesDivisionFilter(u, filter!.division));
      if (includeAll) {
        // keep all matched rows
      } else if (hasExactHours) {
        matched = matched.filter((u) => Math.round(hoursValue(u, field)) === filter!.hours);
      } else if (anyOutstanding) {
        matched = matched.filter((u) => hoursValue(u, field) > 0);
      }
      const q = debouncedSearch.toLowerCase();
      if (q) {
        matched = matched.filter(
          (u) =>
            (u.ceaNumber || '').toLowerCase().includes(q) ||
            (u.name || '').toLowerCase().includes(q) ||
            (u.email || '').toLowerCase().includes(q) ||
            (u.division || '').toLowerCase().includes(q),
        );
      }
      const dir = sortDir === 'asc' ? 1 : -1;
      return [...matched].sort((a, b) => {
        const d = (hoursValue(a, sortBy) - hoursValue(b, sortBy)) * dir;
        if (d !== 0) return d;
        return b.totalOutstanding - a.totalOutstanding;
      });
    }
    const res = await api.getCpdAnalyticsUsers({
      status: filter!.status,
      center: filter!.center,
      division: filter!.division,
      includeAll: !hasExactHours && includeAll ? true : undefined,
      hours: hasExactHours ? filter!.hours : undefined,
      anyOutstanding: !hasExactHours && anyOutstanding ? true : undefined,
      hoursField: field,
      snapshot: filter!.snapshot,
      team: filter!.team,
      search: debouncedSearch || undefined,
      sortBy,
      sortDir,
      exportAll: true,
    });
    return res.users;
  }, [filter, debouncedSearch, sortBy, sortDir, listMode, shortListRange]);

  const handleExport = useCallback(async (format: 'csv' | 'xlsx') => {
    if (!filterHasUserListScope(filter) || exporting) return;
    setExporting(true);
    setError(null);
    try {
      const rows = await loadMatchingAgentRows();
      const exportRows = buildExportRows(rows);
      const safeTitle = (title || subtitle || 'cpd-agents').replace(/[^\w\-]+/g, '_').slice(0, 40);
      const now = new Date();
      const stamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const filename = `${safeTitle}${exportNameSuffix}-${stamp}.${format}`;
      await downloadAgentSheet(exportRows, filename, format);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export agents');
    } finally {
      setExporting(false);
    }
  }, [filter, exporting, loadMatchingAgentRows, buildExportRows, title, subtitle, exportNameSuffix]);

  const buildEmailAttachment = useCallback(async () => {
    const rows = await loadMatchingAgentRows();
    const exportRows = buildExportRows(rows);
    const sheet = XLSX.utils.json_to_sheet(exportRows);
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Agents');
    const safeTitle = (title || subtitle || 'cpd-agents').replace(/[^\w\-]+/g, '_').slice(0, 40);
    const now = new Date();
    const stamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const fileName = `${safeTitle}${exportNameSuffix}-${stamp}.xlsx`;
    const base64 = XLSX.write(book, { bookType: 'xlsx', type: 'base64' }) as string;
    return { base64, fileName, agentCount: rows.length };
  }, [loadMatchingAgentRows, buildExportRows, title, subtitle, exportNameSuffix]);

  const attachmentFileNameHint = useMemo(() => {
    const safeTitle = (title || subtitle || 'cpd-agents').replace(/[^\w\-]+/g, '_').slice(0, 40);
    const now = new Date();
    const stamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    return `${safeTitle}${exportNameSuffix}-${stamp}.xlsx`;
  }, [title, subtitle, exportNameSuffix]);

  const headerCellSx = {
    backgroundColor: 'var(--color-surface-thead)',
    color: 'var(--color-text-strong)',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  } as const;

  const sortLabelSx = {
    color: 'inherit',
    '&.Mui-active': { color: 'inherit' },
    '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
  } as const;

  const cellValue = (u: CpdUserRow, key: UserListSortBy | 'total') => {
    if (key === 'pe') return u.outstandingPE;
    if (key === 'sl') return u.outstandingSL;
    if (key === 'sdl') return u.outstandingSDL;
    return u.totalOutstanding;
  };

  const listBody = (
    <>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={listMode}
          onChange={(_, next: AgentListMode | null) => {
            if (next) setListMode(next);
          }}
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontWeight: 600,
              px: 1.5,
              py: 0.25,
              color: 'rgba(var(--color-text-rgb), 0.7)',
            },
          }}
        >
          <ToggleButton value="long">Long list</ToggleButton>
          <ToggleButton value="short">Short list</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ sm: 'center' }}
        sx={{ mb: 2 }}
      >
        <TextField
          size="small"
          fullWidth
          placeholder="Search by CEA, name, division, or email…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: 'rgba(var(--color-text-rgb), 0.5)' }} />
              </InputAdornment>
            ),
          }}
        />
        {embedded && (
          <Stack direction="row" spacing={1} flexShrink={0} flexWrap="wrap" useFlexGap>
            {onOpenPerformance && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<TrendingUpIcon />}
                disabled={total === 0}
                onClick={onOpenPerformance}
                sx={{ textTransform: 'none' }}
              >
                Performance
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={exporting ? <CircularProgress size={14} /> : <DownloadIcon />}
              disabled={exporting || total === 0}
              onClick={() => handleExport('csv')}
              sx={{ textTransform: 'none' }}
            >
              Export CSV
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={exporting ? <CircularProgress size={14} /> : <DownloadIcon />}
              disabled={exporting || total === 0}
              onClick={() => handleExport('xlsx')}
              sx={{ textTransform: 'none' }}
            >
              Export Excel
            </Button>
            {allowSendEmail && (
              <Button
                size="small"
                variant="contained"
                startIcon={<MailOutlineIcon />}
                disabled={total === 0}
                onClick={() => setEmailDialogOpen(true)}
                sx={{ textTransform: 'none' }}
              >
                Send Email
              </Button>
            )}
          </Stack>
        )}
      </Stack>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
      ) : total === 0 ? (
        <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', py: 4, textAlign: 'center' }}>
          {searchQuery.trim() ? 'No users match your search.' : 'No users in this group.'}
        </Typography>
      ) : (
        <TableContainer sx={{ maxHeight: embedded ? 'min(52vh, 560px)' : 'min(60vh, 640px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellSx}>CEA</TableCell>
                <TableCell sx={headerCellSx}>Name</TableCell>
                <TableCell sx={headerCellSx}>Status</TableCell>
                <TableCell sx={headerCellSx}>Division</TableCell>
                <TableCell sx={headerCellSx}>Mobile</TableCell>
                <TableCell sx={headerCellSx}>Email</TableCell>
                {hourColumns.map((col) => {
                  const sortable = col.key !== 'total';
                  return (
                    <TableCell
                      key={col.key}
                      align="right"
                      sortDirection={sortable && sortBy === col.key ? sortDir : false}
                      sx={headerCellSx}
                    >
                      {sortable ? (
                        <TableSortLabel
                          active={sortBy === col.key}
                          direction={sortBy === col.key ? sortDir : 'desc'}
                          onClick={() => handleSort(col.key)}
                          sx={sortLabelSx}
                        >
                          {col.label}
                        </TableSortLabel>
                      ) : (
                        col.label
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u, idx) => (
                <TableRow key={`${u.ceaNumber}-${page}-${idx}`} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{u.ceaNumber || '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{u.name || '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{u.status || '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{u.division || '-'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{u.mobile || '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{u.email || '—'}</TableCell>
                  {hourColumns.map((col) => (
                    <TableCell
                      key={col.key}
                      align="right"
                      sx={{ fontWeight: col.key === 'total' || col.key === hoursField ? 600 : 400 }}
                    >
                      {cellValue(u, col.key)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  colSpan={colSpan}
                  count={total}
                  page={page}
                  onPageChange={(_, nextPage) => setPage(nextPage)}
                  rowsPerPage={USER_LIST_PAGE_SIZE}
                  onRowsPerPageChange={() => {}}
                  rowsPerPageOptions={[USER_LIST_PAGE_SIZE]}
                  sx={{
                    border: 'none',
                    '& .MuiTablePagination-toolbar': { px: 0 },
                  }}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}
    </>
  );

  const emailDialog = allowSendEmail ? (
    <CpdSendOutstandingEmailDialog
      open={emailDialogOpen}
      onClose={() => setEmailDialogOpen(false)}
      contextLabel={subtitle || title || 'Outstanding CPD hours'}
      attachmentFileNameHint={attachmentFileNameHint}
      buildAttachment={buildEmailAttachment}
    />
  ) : null;

  if (embedded) {
    if (!open) return null;
    return (
      <Box>
        {listBody}
        {emailDialog}
      </Box>
    );
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)' } }}>
        <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <Box>
              <Typography variant="h6">{title}</Typography>
              {(subtitle || total > 0) && (
                <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.72)' }}>
                  {subtitle ? `${subtitle} · ` : ''}
                  {total} {total === 1 ? 'agent' : 'agents'}
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              {onOpenPerformance && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<TrendingUpIcon />}
                  disabled={total === 0}
                  onClick={onOpenPerformance}
                  sx={{ textTransform: 'none' }}
                >
                  Performance
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                startIcon={exporting ? <CircularProgress size={14} /> : <DownloadIcon />}
                disabled={exporting || total === 0}
                onClick={() => handleExport('csv')}
                sx={{ textTransform: 'none' }}
              >
                Export CSV
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={exporting ? <CircularProgress size={14} /> : <DownloadIcon />}
                disabled={exporting || total === 0}
                onClick={() => handleExport('xlsx')}
                sx={{ textTransform: 'none' }}
              >
                Export Excel
              </Button>
              <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </Stack>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {listBody}
        </DialogContent>
      </Dialog>
      {emailDialog}
    </>
  );
};

// ---- Commission / performer drill-down -------------------------------------

const PERFORMANCE_PAGE_SIZE = 25;
const DEFAULT_PERFORMER_BENCHMARK = 10000;

const OUTSTANDING_CATEGORY_LABEL = {
  pe: 'Professional Ethics (PE)',
  sl: 'Salesperson Legislation (SL)',
  sdl: 'Self-Directed Learning (SDL)',
} as const;

const DEFAULT_WA_TEMPLATE = `Hi {name},

Our records show you still have {total} CPD hour(s) outstanding for the {cycle} cycle:
{breakdown}

Your training centre on record is {school}. Please book your remaining sessions early so your CEA registration renewal is not held up.

Thank you!`;

const WA_PLACEHOLDERS = [
  '{name}',
  '{firstName}',
  '{cea}',
  '{total}',
  '{pe}',
  '{sl}',
  '{sdl}',
  '{breakdown}',
  '{school}',
  '{division}',
  '{cycle}',
] as const;

/** Only the categories the agent actually owes hours in, one bullet per line. */
function outstandingBreakdown(u: CpdPerformanceUserRow): string {
  const lines: string[] = [];
  if (u.outstandingPE > 0) lines.push(`• ${OUTSTANDING_CATEGORY_LABEL.pe}: ${u.outstandingPE} hr`);
  if (u.outstandingSL > 0) lines.push(`• ${OUTSTANDING_CATEGORY_LABEL.sl}: ${u.outstandingSL} hr`);
  if (u.outstandingSDL > 0) lines.push(`• ${OUTSTANDING_CATEGORY_LABEL.sdl}: ${u.outstandingSDL} hr`);
  return lines.length > 0 ? lines.join('\n') : '• No category breakdown on record';
}

/** Fill the editable template from one agent's profile. */
function craftWhatsAppMessage(template: string, u: CpdPerformanceUserRow): string {
  const name = (u.name || '').trim();
  const values: Record<string, string> = {
    '{name}': name || 'there',
    '{firstName}': name.split(/\s+/)[0] || 'there',
    '{cea}': u.ceaNumber || '',
    '{total}': String(u.totalOutstanding),
    '{pe}': String(u.outstandingPE),
    '{sl}': String(u.outstandingSL),
    '{sdl}': String(u.outstandingSDL),
    '{breakdown}': outstandingBreakdown(u),
    '{school}': u.school,
    '{division}': u.division || '-',
    '{cycle}': u.cpdCycle || 'current',
  };
  return Object.entries(values).reduce(
    (text, [token, value]) => text.split(token).join(value),
    template,
  );
}

/** Local 8-digit mobiles get the SG country code; already-coded numbers pass through. */
function toWhatsAppNumber(mobile: string | null): string | null {
  const cleaned = (mobile || '').replace(/[\s\-()]/g, '').trim();
  if (!cleaned) return null;
  const bare = cleaned.startsWith('+') ? cleaned.slice(1) : cleaned;
  if (!/^\d{8,15}$/.test(bare)) return null;
  return bare.length === 8 ? `65${bare}` : bare;
}

function waLink(mobile: string | null, message: string): string | null {
  const num = toWhatsAppNumber(mobile);
  return num ? `https://wa.me/${num}?text=${encodeURIComponent(message)}` : null;
}

const SGD_FORMAT = new Intl.NumberFormat('en-SG', {
  style: 'currency',
  currency: 'SGD',
  maximumFractionDigits: 0,
});

/** Same grouping as SGD_FORMAT but without the symbol — the base for masked amounts. */
const GROUPED_FORMAT = new Intl.NumberFormat('en-SG', { maximumFractionDigits: 0 });

function formatSgd(value: number): string {
  return SGD_FORMAT.format(Math.round(value));
}

/** Commission with the thousands and above masked out, so the agent table stays
 *  shareable (screenshots, screen-shares) without exposing what anyone earned.
 *  Digit count and comma grouping survive, so the magnitude still reads:
 *  12,345 → xx,345 · 123,456 → xxx,456 · 1,234,567 → x,xxx,567.
 *  Under a thousand there is nothing above the hundreds group, so it shows as-is. */
function maskSgd(value: number): string {
  const grouped = GROUPED_FORMAT.format(Math.abs(Math.round(value)));
  const digitCount = grouped.replace(/\D/g, '').length;
  if (digitCount <= 3) return grouped;
  const maskUpTo = digitCount - 3;
  let seen = 0;
  return grouped.replace(/\d/g, (digit) => {
    seen += 1;
    return seen <= maskUpTo ? 'x' : digit;
  });
}

/**
 * One CSV row of agent detail, shared by the segment-wide export on the income
 * distribution card and the drill-down list export. The amount stays masked, so
 * a sheet reveals no more than the table it came from.
 */
function performanceExportRow(
  u: CpdPerformanceUserRow,
  opts: {
    template: string;
    incomeLabel: string;
    showIncome: boolean;
    startMonth: string;
    endMonth: string;
    /** Band label per `CpdPerformanceUserRow.band`. Omit to drop the column. */
    bandLabels?: Map<string, string>;
  },
): Record<string, string | number> {
  const message = craftWhatsAppMessage(opts.template, u);
  return {
    Name: u.name || '',
    CEA: u.ceaNumber || '',
    Mobile: u.mobile || '',
    Email: u.email || '',
    Division: u.division || '-',
    School: u.school,
    'CPD Status': u.status,
    'CPD Cycle': u.cpdCycle || '',
    'Outstanding Total': u.totalOutstanding,
    'Outstanding PE': u.outstandingPE,
    'Outstanding SL': u.outstandingSL,
    'Outstanding SDL': u.outstandingSDL,
    ...(opts.showIncome
      ? { [`${opts.incomeLabel} ${opts.startMonth} to ${opts.endMonth} (masked)`]: maskSgd(u.income) }
      : {}),
    // A band names an income range, so it is held back wherever amounts are.
    ...(opts.showIncome && opts.bandLabels
      ? { 'Income Band': opts.bandLabels.get(u.band) ?? u.band }
      : {}),
    Performer: u.performer ? 'Yes' : 'No',
    'WhatsApp Link': waLink(u.mobile, message) || '',
    'WhatsApp Message': message,
  };
}

/**
 * Write agent rows out as a single-sheet workbook download.
 *
 * Goes through `saveBlobDownload` rather than `XLSX.writeFile` so native
 * WebViews keep the file name and extension instead of saving a nameless
 * `.bin` blob.
 */
async function downloadAgentSheet(
  rows: Record<string, string | number>[],
  fileName: string,
  format: 'csv' | 'xlsx' = 'csv',
): Promise<void> {
  const sheet = XLSX.utils.json_to_sheet(rows);
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, 'Agents');
  const mime =
    format === 'csv'
      ? 'text/csv'
      : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const data = XLSX.write(book, { bookType: format, type: 'array' }) as ArrayBuffer;
  await saveBlobDownload(new Blob([data], { type: mime }), fileName, mime);
}

/** `YYYY-MM`, the value shape of `<input type="month">`. */
function monthInputValue(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function defaultPerformanceRange(): { startMonth: string; endMonth: string } {
  const now = new Date();
  return { startMonth: `${now.getFullYear()}-01`, endMonth: monthInputValue(now) };
}

/**
 * Sequential blue ramp for income magnitude.
 *
 * Deliberately not the status green/red — those stay reserved for CPD pass/fail,
 * so an income band never reads as a verdict. Monotonic in lightness by
 * construction and every step clears 3:1 on its own surface (light ≥ 3.26:1,
 * dark ≥ 3.06:1).
 */
function colorForIncomeBand(index: number, bandCount: number, isDark: boolean): string {
  const ratio = bandCount > 1 ? Math.min(1, Math.max(0, index / (bandCount - 1))) : 0;
  if (isDark) {
    return `hsl(216, ${(42 + 46 * ratio).toFixed(0)}%, ${(52 + 28 * ratio).toFixed(0)}%)`;
  }
  return `hsl(222, ${(54 + 26 * ratio).toFixed(0)}%, ${(61 - 27 * ratio).toFixed(0)}%)`;
}

/** Income-band histogram. Magnitude on an ordered axis → one sequential hue. */
const CpdIncomeBandChart: React.FC<{
  bands: CpdIncomeBand[];
  benchmark: number;
  onBandClick?: (band: CpdIncomeBand) => void;
  height?: number;
}> = ({ bands, benchmark, onBandClick, height = 300 }) => {
  const theme = useChartTheme();
  const total = bands.reduce((sum, b) => sum + b.count, 0);
  // Left edge of the first band that sits wholly at/above the benchmark.
  const performerBandKey = bands.find((b) => b.key !== 'zero' && b.min >= benchmark)?.key ?? null;

  const renderTooltip = useCallback(
    (props: unknown) => {
      const { active, payload } = props as RechartsTooltipContentProps;
      if (!active || !payload?.length) return null;
      const row = payload[0]?.payload as CpdIncomeBand | undefined;
      if (!row) return null;
      return (
        <ChartTooltipBox
          isDark={theme.isDark}
          title={row.label}
          rows={[
            { name: 'Agents', value: formatCountWithPct(row.count, total) },
            {
              name: 'Income',
              value:
                row.key === 'zero'
                  ? 'No earnings in range'
                  : `${formatSgd(row.min)} – ${row.max === null ? 'above' : formatSgd(row.max)}`,
            },
          ]}
          footer={onBandClick ? 'Click anywhere in this column to list these agents' : undefined}
        />
      );
    },
    [theme, total, onBandClick],
  );

  /**
   * Click anywhere in a column, not just on the bar. The low bands are only a few
   * pixels tall, so the bar alone is an unusable target — Recharts already resolves
   * the column under the pointer for the tooltip cursor, so the same index drives
   * the click.
   */
  const handleChartClick = useCallback(
    // Recharts types the active index loosely (it can arrive as a string), so
    // normalise before indexing.
    (state: { activeTooltipIndex?: number | string | null }) => {
      if (!onBandClick) return;
      const index = Number(state?.activeTooltipIndex);
      if (!Number.isInteger(index)) return;
      const band = bands[index];
      if (band) onBandClick(band);
    },
    [onBandClick, bands],
  );

  if (total === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', py: 4, textAlign: 'center' }}>
        No agents in this segment.
      </Typography>
    );
  }

  return (
    <Box>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={bands}
          margin={{ top: 18, right: 16, left: 0, bottom: 22 }}
          onClick={onBandClick ? handleChartClick : undefined}
          style={onBandClick ? { cursor: 'pointer' } : undefined}
        >
          <CartesianGrid stroke={theme.grid} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="label"
            stroke={theme.axis}
            tick={{ ...theme.tick, fontSize: 11 }}
            tickLine={false}
            interval={0}
            angle={-18}
            textAnchor="end"
            height={54}
          />
          <YAxis
            allowDecimals={false}
            stroke={theme.axis}
            tick={theme.tick}
            tickLine={false}
            label={{
              value: 'Agents',
              angle: -90,
              position: 'insideLeft',
              style: { fill: theme.tick.fill, fontSize: 12 },
            }}
          />
          <RechartsTooltip cursor={{ fill: theme.hoverCursorFill }} content={renderTooltip} />
          {performerBandKey && (
            <ReferenceLine
              x={bands.find((b) => b.key === performerBandKey)?.label}
              stroke={theme.status.Passed}
              strokeDasharray="4 3"
              strokeWidth={2}
              label={{
                value: `${formatSgd(benchmark)} performer line`,
                position: 'top',
                style: { fill: theme.status.Passed, fontSize: 11, fontWeight: 700 },
              }}
            />
          )}
          <Bar
            dataKey="count"
            name="Agents"
            radius={[4, 4, 0, 0]}
            stroke={theme.surface}
            strokeWidth={2}
            maxBarSize={72}
            cursor={onBandClick ? 'pointer' : undefined}
          >
            {bands.map((band, idx) => (
              <Cell key={band.key} fill={colorForIncomeBand(idx, bands.length, theme.isDark)} />
            ))}
            <LabelList
              dataKey="count"
              position="top"
              formatter={formatBarLabel}
              style={{ fill: theme.tick.fill, fontSize: 11, fontWeight: 600 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      {/* On-screen reading aid only — the click hint is meaningless in a PNG, so it
          stays out of the export. */}
      <Typography data-html2canvas-ignore="true" variant="caption" sx={chartHintSx}>
        Deeper blue means a higher income band · the dashed line marks where the performer benchmark falls
        {onBandClick ? ' · click anywhere in a column to list its agents' : ''}
      </Typography>
    </Box>
  );
};

/**
 * Performer vs non-performer proportion. Two values only, so a slim 100% bar
 * plus tiles beats a full chart — and each segment is its own drill-in target.
 */
const CpdPerformerSplitBar: React.FC<{
  performers: number;
  nonPerformers: number;
  benchmark: number;
  /** How the caption names the money — "commission", or "earnings" for Team CPD. */
  incomeNoun?: string;
  /** Period wording for the caption; the range is fixed in Team CPD. */
  periodLabel?: string;
  onSegmentClick?: (group: 'performer' | 'non-performer') => void;
}> = ({
  performers,
  nonPerformers,
  benchmark,
  incomeNoun = 'commission',
  periodLabel = 'over the selected months',
  onSegmentClick,
}) => {
  const theme = useChartTheme();
  const total = performers + nonPerformers;
  if (total === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', py: 3, textAlign: 'center' }}>
        No agents in this segment.
      </Typography>
    );
  }

  const segments: Array<{
    group: 'performer' | 'non-performer';
    label: string;
    count: number;
    color: string;
  }> = [
    { group: 'performer', label: 'Performer', count: performers, color: theme.status.Passed },
    { group: 'non-performer', label: 'Non-performer', count: nonPerformers, color: theme.status.Ongoing },
  ];

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
        {segments.map((seg) => {
          const pct = (seg.count / total) * 100;
          return (
            <Box
              key={seg.group}
              onClick={onSegmentClick ? () => onSegmentClick(seg.group) : undefined}
              sx={{
                flex: 1,
                p: 1.5,
                borderRadius: 2,
                border: '1px solid rgba(var(--color-border-rgb), 0.18)',
                bgcolor: hexToRgba(seg.color, theme.isDark ? 0.14 : 0.09),
                cursor: onSegmentClick ? 'pointer' : 'default',
                transition: 'border-color 120ms ease',
                '&:hover': onSegmentClick ? { borderColor: seg.color } : undefined,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: seg.color, flexShrink: 0 }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'rgba(var(--color-text-rgb), 0.72)' }}>
                  {seg.label}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, lineHeight: 1.2, color: 'var(--color-text-strong)' }}>
                {pct.toFixed(1)}%
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>
                {seg.count.toLocaleString()} of {total.toLocaleString()} agents
              </Typography>
            </Box>
          );
        })}
      </Stack>

      {/* 2px surface gap between fills keeps the boundary readable. */}
      <Box sx={{ display: 'flex', gap: '2px', height: 40, borderRadius: 1.5, overflow: 'hidden' }}>
        {segments.map((seg) => (
          <Tooltip
            key={seg.group}
            title={`${seg.label}: ${seg.count.toLocaleString()} (${((seg.count / total) * 100).toFixed(1)}%)`}
          >
            <Box
              onClick={onSegmentClick ? () => onSegmentClick(seg.group) : undefined}
              sx={{
                flexGrow: Math.max(seg.count, 0),
                flexBasis: 0,
                minWidth: seg.count > 0 ? 44 : 0,
                bgcolor: seg.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.onStatusInk,
                fontWeight: 800,
                fontSize: 13,
                cursor: onSegmentClick ? 'pointer' : 'default',
              }}
            >
              {seg.count > 0 ? `${((seg.count / total) * 100).toFixed(1)}%` : ''}
            </Box>
          </Tooltip>
        ))}
      </Box>
      <Typography variant="caption" sx={chartHintSx}>
        Performer = {formatSgd(benchmark)} or more in {incomeNoun} {periodLabel}
        {onSegmentClick ? ' · click a segment to list the agents' : ''}
      </Typography>
    </Box>
  );
};

/** One agent's WhatsApp message, pre-filled from their profile and editable before sending. */
const CpdWhatsAppMessageDialog: React.FC<{
  open: boolean;
  user: CpdPerformanceUserRow | null;
  initialMessage: string;
  onClose: () => void;
}> = ({ open, user, initialMessage, onClose }) => {
  const [message, setMessage] = useState(initialMessage);

  useEffect(() => {
    if (open) setMessage(initialMessage);
  }, [open, initialMessage]);

  const link = user ? waLink(user.mobile, message) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)' } }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography variant="h6">WhatsApp {user?.name || user?.ceaNumber || 'agent'}</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.72)' }}>
              {user?.mobile ? `${user.mobile} · ` : 'No mobile on record · '}
              {user?.totalOutstanding ?? 0} hr outstanding
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <TextField
          multiline
          minRows={9}
          fullWidth
          label="Message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
        {!link && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            This agent has no usable mobile number on record, so WhatsApp cannot be opened.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={<WhatsAppIcon />}
          disabled={!link}
          onClick={() => {
            if (link) window.open(link, '_blank', 'noopener,noreferrer');
            onClose();
          }}
        >
          Open WhatsApp
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface PerformanceDrill {
  title: string;
  group?: 'performer' | 'non-performer';
  band?: string;
}

type PerformanceSortBy = CpdPerformanceSortBy;

/** Every column sorts; `kind` picks which filter control sits under its header. */
interface PerformanceColumn {
  key: PerformanceSortBy;
  filterKey: keyof CpdPerformanceColumnFilters;
  label: string;
  align?: 'right';
  kind: 'text' | 'number' | 'school' | 'performer';
}

const PERFORMANCE_COLUMNS: PerformanceColumn[] = [
  { key: 'name', filterKey: 'name', label: 'Name', kind: 'text' },
  { key: 'cea', filterKey: 'cea', label: 'CEA', kind: 'text' },
  { key: 'mobile', filterKey: 'mobile', label: 'Mobile', kind: 'text' },
  { key: 'division', filterKey: 'division', label: 'Division', kind: 'text' },
  { key: 'school', filterKey: 'school', label: 'School', kind: 'school' },
  { key: 'total', filterKey: 'total', label: 'Outstanding', align: 'right', kind: 'number' },
  { key: 'pe', filterKey: 'pe', label: 'PE', align: 'right', kind: 'number' },
  { key: 'sl', filterKey: 'sl', label: 'SL', align: 'right', kind: 'number' },
  { key: 'sdl', filterKey: 'sdl', label: 'SDL', align: 'right', kind: 'number' },
  { key: 'income', filterKey: 'income', label: 'Commission', align: 'right', kind: 'number' },
  { key: 'performer', filterKey: 'performer', label: 'Performer', kind: 'performer' },
];

const DEFAULT_INCOME_LABEL = 'Commission';

/**
 * The table's columns: income relabelled (Team CPD calls it "Earning This Year"),
 * or dropped altogether where the amount is not for the viewer's eyes.
 */
function performanceColumns(incomeLabel: string, showIncome: boolean): PerformanceColumn[] {
  if (!showIncome) return PERFORMANCE_COLUMNS.filter((col) => col.key !== 'income');
  if (incomeLabel === DEFAULT_INCOME_LABEL) return PERFORMANCE_COLUMNS;
  return PERFORMANCE_COLUMNS.map((col) =>
    col.key === 'income' ? { ...col, label: incomeLabel } : col,
  );
}

const NUMERIC_FILTER_HELP =
  'Compare or range: >25000 · >=10 · <5000 · <=4 · =16 · !=0 · 1000-5000 · >1k <5k (k/m and $ , allowed)';

const TEXT_FILTER_KEYS = ['name', 'cea', 'mobile', 'division'] as const;
const NUMBER_FILTER_KEYS = ['total', 'pe', 'sl', 'sdl', 'income'] as const;

/** Drop blanks and any numeric expression the grammar rejects, so a half-typed
 *  `>` never round-trips to the server. */
function sanitizePerformanceFilters(f: CpdPerformanceColumnFilters): CpdPerformanceColumnFilters {
  const out: CpdPerformanceColumnFilters = {};
  for (const key of TEXT_FILTER_KEYS) {
    const value = (f[key] || '').trim();
    if (value) out[key] = value;
  }
  if (f.school) out.school = f.school;
  if (f.performer) out.performer = f.performer;
  for (const key of NUMBER_FILTER_KEYS) {
    const value = (f[key] || '').trim();
    // Needs at least one digit — a lone "$" or "," reduces to an empty filter
    // server-side, so sending it would be a dead query param.
    if (value && /\d/.test(value) && isValidNumericFilter(value)) out[key] = value;
  }
  return out;
}

function countActiveFilters(f: CpdPerformanceColumnFilters): number {
  return Object.values(f).filter((v) => typeof v === 'string' && v.trim() !== '').length;
}

/** Agent detail behind a performer segment or income band, with WhatsApp follow-up. */
const CpdPerformanceUserListDialog: React.FC<{
  open: boolean;
  subtitle?: string;
  filter: UserListFilter | null;
  drill: PerformanceDrill | null;
  startMonth: string;
  endMonth: string;
  benchmark: number;
  /** Header wording for the income column. Team CPD reads it as earnings. */
  incomeLabel?: string;
  /**
   * Whether the per-agent amount is shown at all. Off for Team CPD: the charts
   * above still split on the $10,000 line and the Performer flag still says which
   * side each agent falls on, but the table does not name anyone's figure.
   */
  showIncome?: boolean;
  /** Team leaders send the standard message; only admins may rewrite the template. */
  allowTemplateEdit?: boolean;
  onClose: () => void;
}> = ({
  open,
  subtitle,
  filter,
  drill,
  startMonth,
  endMonth,
  benchmark,
  incomeLabel = DEFAULT_INCOME_LABEL,
  showIncome = true,
  allowTemplateEdit = true,
  onClose,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  // Income is the most useful default sort, but it has to be a visible column.
  const defaultSortBy: PerformanceSortBy = showIncome ? 'income' : 'total';
  const [sortBy, setSortBy] = useState<PerformanceSortBy>(defaultSortBy);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<CpdPerformanceUserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [template, setTemplate] = useState(DEFAULT_WA_TEMPLATE);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [messageTarget, setMessageTarget] = useState<CpdPerformanceUserRow | null>(null);
  const [filters, setFilters] = useState<CpdPerformanceColumnFilters>({});
  const [appliedFilters, setAppliedFilters] = useState<CpdPerformanceColumnFilters>({});
  // Sticky offset for the filter row — measured so it can't drift from the
  // header row's real height across themes and font sizes.
  const headRowRef = useRef<HTMLTableRowElement>(null);
  const [headRowHeight, setHeadRowHeight] = useState(37);

  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setDebouncedSearch('');
      setPage(0);
      setSortBy(defaultSortBy);
      setSortDir('desc');
      setUsers([]);
      setTotal(0);
      setError(null);
      setExporting(false);
      setTemplateOpen(false);
      setMessageTarget(null);
      setFilters({});
      setAppliedFilters({});
    }
  }, [open, defaultSortBy]);

  // Debounced so typing a filter fires one query; guarded on value so a fresh
  // object identity alone does not re-trigger the fetch.
  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => {
      const next = sanitizePerformanceFilters(filters);
      setAppliedFilters((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    }, 300);
    return () => window.clearTimeout(timer);
  }, [open, filters]);

  useEffect(() => {
    const el = headRowRef.current;
    if (el) setHeadRowHeight(el.getBoundingClientRect().height || 37);
  }, [users.length, loading]);

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => setDebouncedSearch(searchQuery.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [open, searchQuery]);

  useEffect(() => {
    setPage(0);
  }, [
    debouncedSearch,
    sortBy,
    sortDir,
    drill?.group,
    drill?.band,
    startMonth,
    endMonth,
    benchmark,
    appliedFilters,
  ]);

  const baseQuery = useMemo<CpdPerformanceUsersQuery | null>(() => {
    if (!filter || !drill) return null;
    return {
      status: filter.status,
      center: filter.center,
      division: filter.division,
      hours: filter.hours,
      hoursField: filter.hoursField,
      includeAll: filter.includeAll,
      snapshot: filter.snapshot,
      team: filter.team,
      startDate: startMonth,
      endDate: endMonth,
      benchmark,
      group: drill.group,
      band: drill.band,
      filters: appliedFilters,
    };
  }, [filter, drill, startMonth, endMonth, benchmark, appliedFilters]);

  useEffect(() => {
    if (!open || !baseQuery) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.getCpdPerformanceUsers({
          ...baseQuery,
          search: debouncedSearch || undefined,
          page,
          pageSize: PERFORMANCE_PAGE_SIZE,
          sortBy,
          sortDir,
        });
        if (!cancelled) {
          setUsers(res.users);
          setTotal(res.total);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load agents');
          setUsers([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, baseQuery, debouncedSearch, page, sortBy, sortDir]);

  const handleSort = useCallback((column: PerformanceSortBy) => {
    setSortBy((prev) => {
      if (prev === column) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir(column === 'name' || column === 'division' ? 'asc' : 'desc');
      return column;
    });
  }, []);

  const handleExportCsv = useCallback(async () => {
    if (!baseQuery || exporting) return;
    setExporting(true);
    setError(null);
    try {
      const res = await api.getCpdPerformanceUsers({
        ...baseQuery,
        search: debouncedSearch || undefined,
        sortBy,
        sortDir,
        exportAll: true,
      });
      const rows = res.users.map((u) =>
        performanceExportRow(u, { template, incomeLabel, showIncome, startMonth, endMonth }),
      );
      const safeTitle = (drill?.title || 'cpd-performance').replace(/[^\w\-]+/g, '_').slice(0, 40);
      await downloadAgentSheet(rows, `${safeTitle}-${startMonth}_to_${endMonth}.csv`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export agents');
    } finally {
      setExporting(false);
    }
  }, [
    baseQuery,
    exporting,
    debouncedSearch,
    sortBy,
    sortDir,
    template,
    drill,
    startMonth,
    endMonth,
    incomeLabel,
    showIncome,
  ]);

  const headerCellSx = {
    backgroundColor: 'var(--color-surface-thead)',
    color: 'var(--color-text-strong)',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  } as const;

  const sortLabelSx = {
    color: 'inherit',
    '&.Mui-active': { color: 'inherit' },
    '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
  } as const;

  const theme = useChartTheme();
  const activeFilterCount = countActiveFilters(filters);
  const columns = useMemo(
    () => performanceColumns(incomeLabel, showIncome),
    [incomeLabel, showIncome],
  );

  const setFilterValue = useCallback(
    (key: keyof CpdPerformanceColumnFilters, value: string) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  /** Filter row cell background must be opaque — body rows scroll under it. */
  const filterCellSx = {
    backgroundColor: 'var(--color-surface-thead)',
    top: headRowHeight,
    py: 0.5,
  } as const;

  const filterInputSx = {
    '& .MuiInputBase-input': { fontSize: '0.78rem', py: 0.5 },
    '& .MuiInputBase-root': { bgcolor: 'var(--color-surface)' },
  } as const;

  const renderFilterControl = (col: PerformanceColumn) => {
    const raw = filters[col.filterKey] || '';

    if (col.kind === 'school' || col.kind === 'performer') {
      const options =
        col.kind === 'school'
          ? [
              { value: '', label: 'All' },
              { value: 'Hastors', label: 'Hastors' },
              { value: 'Huttons', label: 'Huttons' },
              { value: 'Others', label: 'Others' },
            ]
          : [
              { value: '', label: 'All' },
              { value: 'yes', label: 'Yes' },
              { value: 'no', label: 'No' },
            ];
      return (
        <TextField
          select
          size="small"
          fullWidth
          value={raw}
          onChange={(e) => setFilterValue(col.filterKey, e.target.value)}
          SelectProps={{ displayEmpty: true }}
          sx={{ ...filterInputSx, minWidth: 92 }}
        >
          {options.map((opt) => (
            <MenuItem key={opt.value || 'all'} value={opt.value} sx={{ fontSize: '0.8rem' }}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    const isNumber = col.kind === 'number';
    const invalid = isNumber && raw.trim() !== '' && !isValidNumericFilter(raw);
    return (
      <Tooltip title={isNumber ? NUMERIC_FILTER_HELP : `Filter ${col.label} by text`} placement="top">
        <TextField
          size="small"
          fullWidth
          value={raw}
          error={invalid}
          placeholder={isNumber ? '> 0' : 'contains…'}
          onChange={(e) => setFilterValue(col.filterKey, e.target.value)}
          inputProps={{
            'aria-label': `Filter by ${col.label}`,
            style: isNumber ? { textAlign: 'right' } : undefined,
          }}
          sx={{ ...filterInputSx, minWidth: isNumber ? 88 : 104 }}
        />
      </Tooltip>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)' } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6">{drill?.title ?? ''}</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.72)' }}>
                {subtitle ? `${subtitle} · ` : ''}
                {total.toLocaleString()} {total === 1 ? 'agent' : 'agents'} · {incomeLabel.toLowerCase()} {startMonth} to {endMonth} · performer ≥ {formatSgd(benchmark)}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
              <Button
                size="small"
                variant="outlined"
                startIcon={exporting ? <CircularProgress size={14} /> : <DownloadIcon />}
                disabled={exporting || total === 0}
                onClick={handleExportCsv}
                sx={{ textTransform: 'none' }}
              >
                Export CSV
              </Button>
              <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </Stack>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 1.5 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search by name, CEA, mobile, division, or email…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'rgba(var(--color-text-rgb), 0.5)' }} />
                  </InputAdornment>
                ),
              }}
            />
            {allowTemplateEdit && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setTemplateOpen((v) => !v)}
                sx={{ textTransform: 'none', flexShrink: 0 }}
              >
                {templateOpen ? 'Hide message template' : 'Edit message template'}
              </Button>
            )}
          </Stack>

          {allowTemplateEdit && templateOpen && (
            <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
              <Typography variant="subtitle2" sx={sectionTitleSx}>WhatsApp message template</Typography>
              <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)', display: 'block', mb: 1 }}>
                Each agent's message is built from this template. <code>{'{breakdown}'}</code> expands to only the
                categories that agent still owes hours in.
              </Typography>
              <TextField
                multiline
                minRows={8}
                fullWidth
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                sx={{ mb: 1 }}
              />
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                {WA_PLACEHOLDERS.map((token) => (
                  <Chip
                    key={token}
                    label={token}
                    size="small"
                    onClick={() => setTemplate((prev) => `${prev}${token}`)}
                    sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                  />
                ))}
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button size="small" onClick={() => setTemplate(DEFAULT_WA_TEMPLATE)} sx={{ textTransform: 'none' }}>
                  Reset to default
                </Button>
              </Stack>
              {users.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>
                    Preview for {users[0].name || users[0].ceaNumber}:
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.5,
                      p: 1.25,
                      borderRadius: 1.5,
                      bgcolor: 'rgba(var(--color-text-rgb), 0.04)',
                      border: '1px solid rgba(var(--color-border-rgb), 0.16)',
                      whiteSpace: 'pre-wrap',
                      fontSize: '0.8rem',
                      color: 'rgba(var(--color-text-rgb), 0.85)',
                    }}
                  >
                    {craftWhatsAppMessage(template, users[0])}
                  </Box>
                </Box>
              )}
            </Paper>
          )}

          {activeFilterCount > 0 && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Chip
                size="small"
                label={`${activeFilterCount} column filter${activeFilterCount === 1 ? '' : 's'} active`}
                onDelete={() => setFilters({})}
                deleteIcon={<FilterAltOffIcon />}
              />
              <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                {total.toLocaleString()} matching
              </Typography>
            </Stack>
          )}

          {/* The table (and its filter row) stays mounted even with zero matches —
              otherwise a filter that matches nothing would remove the only way to
              clear it. */}
          <TableContainer sx={{ maxHeight: 'min(58vh, 620px)' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow ref={headRowRef}>
                    {columns.map((col) => (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        sortDirection={sortBy === col.key ? sortDir : false}
                        sx={headerCellSx}
                      >
                        <TableSortLabel
                          active={sortBy === col.key}
                          direction={sortBy === col.key ? sortDir : 'desc'}
                          onClick={() => handleSort(col.key)}
                          sx={sortLabelSx}
                        >
                          {col.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={headerCellSx}>WhatsApp</TableCell>
                  </TableRow>
                  <TableRow>
                    {columns.map((col) => (
                      <TableCell key={col.key} align={col.align} sx={filterCellSx}>
                        {renderFilterControl(col)}
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={filterCellSx}>
                      {activeFilterCount > 0 && (
                        <Tooltip title="Clear all column filters">
                          <IconButton size="small" onClick={() => setFilters({})}>
                            <FilterAltOffIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && (
                    <TableRow>
                      <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 5, border: 0 }}>
                        <CircularProgress size={26} />
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && total === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length + 1}
                        align="center"
                        sx={{ py: 5, border: 0, color: 'rgba(var(--color-text-rgb), 0.6)' }}
                      >
                        {activeFilterCount > 0 || searchQuery.trim()
                          ? 'No agents match the current filters.'
                          : 'No agents in this group.'}
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && users.map((u, idx) => {
                    const message = craftWhatsAppMessage(template, u);
                    const link = waLink(u.mobile, message);
                    return (
                      <TableRow key={`${u.ceaNumber}-${page}-${idx}`} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{u.name || '—'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{u.ceaNumber || '—'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{u.mobile || '—'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{u.division || '-'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{u.school}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{u.totalOutstanding}</TableCell>
                        <TableCell align="right">{u.outstandingPE}</TableCell>
                        <TableCell align="right">{u.outstandingSL}</TableCell>
                        <TableCell align="right">{u.outstandingSDL}</TableCell>
                        {showIncome && (
                          <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                            {maskSgd(u.income)}
                          </TableCell>
                        )}
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.6,
                              fontWeight: 700,
                              fontSize: '0.75rem',
                              color: u.performer ? theme.status.Passed : theme.status.Ongoing,
                            }}
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: u.performer ? theme.status.Passed : theme.status.Ongoing,
                              }}
                            />
                            {u.performer ? 'Yes' : 'No'}
                          </Box>
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title={link ? 'Open WhatsApp with the auto-crafted message' : 'No usable mobile on record'}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={!link}
                                onClick={() => {
                                  if (link) window.open(link, '_blank', 'noopener,noreferrer');
                                }}
                                sx={{ color: '#25D366' }}
                              >
                                <WhatsAppIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Edit this message before sending">
                            <IconButton size="small" onClick={() => setMessageTarget(u)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      colSpan={columns.length + 1}
                      count={total}
                      page={page}
                      onPageChange={(_, nextPage) => setPage(nextPage)}
                      rowsPerPage={PERFORMANCE_PAGE_SIZE}
                      onRowsPerPageChange={() => {}}
                      rowsPerPageOptions={[PERFORMANCE_PAGE_SIZE]}
                      sx={{ border: 'none', '& .MuiTablePagination-toolbar': { px: 0 } }}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
        </DialogContent>
      </Dialog>

      <CpdWhatsAppMessageDialog
        open={messageTarget !== null}
        user={messageTarget}
        initialMessage={messageTarget ? craftWhatsAppMessage(template, messageTarget) : ''}
        onClose={() => setMessageTarget(null)}
      />
    </>
  );
};

/**
 * Commission profile of one chart segment: income distribution + performer split
 * over a chosen month range, both drillable to the agents behind them.
 */
const CpdPerformancePanel: React.FC<{
  open: boolean;
  subtitle?: string;
  filter: UserListFilter | null;
  /**
   * When true the month range and the performer benchmark are not editable: the
   * panel reports year-to-date earnings against the fixed $10,000 line. This is
   * the Team CPD reading, where the benchmark is company policy rather than a
   * knob for the leader to turn.
   */
  fixedYearToDate?: boolean;
  /** Header wording for the income column and its captions. */
  incomeLabel?: string;
  /** Whether the agent table carries a per-agent amount column at all. */
  showIncome?: boolean;
  allowTemplateEdit?: boolean;
}> = ({
  open,
  subtitle,
  filter,
  fixedYearToDate = false,
  incomeLabel = DEFAULT_INCOME_LABEL,
  showIncome = true,
  allowTemplateEdit = true,
}) => {
  const initialRange = useMemo(() => defaultPerformanceRange(), []);
  const [startMonth, setStartMonth] = useState(initialRange.startMonth);
  const [endMonth, setEndMonth] = useState(initialRange.endMonth);
  const [benchmarkInput, setBenchmarkInput] = useState(String(DEFAULT_PERFORMER_BENCHMARK));
  const [applied, setApplied] = useState({
    startMonth: initialRange.startMonth,
    endMonth: initialRange.endMonth,
    benchmark: DEFAULT_PERFORMER_BENCHMARK,
  });
  const [data, setData] = useState<CpdPerformanceResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drill, setDrill] = useState<PerformanceDrill | null>(null);
  const [exporting, setExporting] = useState(false);
  const incomeCardRef = useRef<HTMLDivElement | null>(null);

  // Debounced so typing a benchmark ("1" → "10" → "10000") fires one query. Only
  // commits when a value actually changed — a new object identity alone would
  // re-trigger the fetch below.
  useEffect(() => {
    if (!open || fixedYearToDate) return;
    const timer = window.setTimeout(() => {
      const parsed = Number(benchmarkInput.replace(/[^0-9.]/g, ''));
      const benchmark = Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_PERFORMER_BENCHMARK;
      setApplied((prev) =>
        prev.startMonth === startMonth && prev.endMonth === endMonth && prev.benchmark === benchmark
          ? prev
          : { startMonth, endMonth, benchmark },
      );
    }, 500);
    return () => window.clearTimeout(timer);
  }, [open, fixedYearToDate, startMonth, endMonth, benchmarkInput]);

  useEffect(() => {
    if (!open || !filter) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.getCpdPerformance({
          status: filter.status,
          center: filter.center,
          division: filter.division,
          hours: filter.hours,
          hoursField: filter.hoursField,
          includeAll: filter.includeAll,
          snapshot: filter.snapshot,
          team: filter.team,
          startDate: applied.startMonth,
          endDate: applied.endMonth,
          benchmark: applied.benchmark,
        });
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load earnings analytics');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    open,
    filter?.status,
    filter?.center,
    filter?.division,
    filter?.hours,
    filter?.hoursField,
    filter?.includeAll,
    filter?.snapshot,
    filter?.team,
    applied,
  ]);

  const summary = data?.summary;

  // Every agent behind the chart in one sheet: no group or band narrowing, so
  // performers and non-performers come down together.
  const handleExportCsv = useCallback(async () => {
    if (!filter || exporting) return;
    setExporting(true);
    setError(null);
    try {
      const res = await api.getCpdPerformanceUsers({
        status: filter.status,
        center: filter.center,
        division: filter.division,
        hours: filter.hours,
        hoursField: filter.hoursField,
        includeAll: filter.includeAll,
        snapshot: filter.snapshot,
        team: filter.team,
        startDate: applied.startMonth,
        endDate: applied.endMonth,
        benchmark: applied.benchmark,
        sortBy: showIncome ? 'income' : 'total',
        sortDir: 'desc',
        exportAll: true,
      });
      const bandLabels = new Map((data?.bands ?? []).map((b) => [b.key, b.label]));
      const rows = res.users.map((u) =>
        performanceExportRow(u, {
          template: DEFAULT_WA_TEMPLATE,
          incomeLabel,
          showIncome,
          startMonth: applied.startMonth,
          endMonth: applied.endMonth,
          bandLabels,
        }),
      );
      await downloadAgentSheet(
        rows,
        `cpd-income-distribution-${applied.startMonth}_to_${applied.endMonth}.csv`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export agents');
    } finally {
      setExporting(false);
    }
  }, [filter, exporting, applied, incomeLabel, showIncome, data?.bands]);

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
        {fixedYearToDate ? (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label={`${incomeLabel}: ${applied.startMonth} to ${applied.endMonth}`}
              sx={{ fontWeight: 600 }}
            />
            <Chip size="small" label={`Performer ≥ ${formatSgd(applied.benchmark)}`} sx={{ fontWeight: 600 }} />
            {loading && <CircularProgress size={18} />}
          </Stack>
        ) : (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-end' }}>
            <TextField
              size="small"
              type="month"
              label="From"
              value={startMonth}
              onChange={(e) => setStartMonth(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />
            <TextField
              size="small"
              type="month"
              label="To"
              value={endMonth}
              onChange={(e) => setEndMonth(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 150 }}
            />
            <TextField
              size="small"
              label="Performer benchmark"
              value={benchmarkInput}
              onChange={(e) => setBenchmarkInput(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              sx={{ minWidth: 190 }}
              helperText="Commission at or above this counts as a performer"
            />
            {loading && <CircularProgress size={20} sx={{ mb: 1 }} />}
          </Stack>
        )}
        <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', display: 'block', mt: 1 }}>
          Earnings are recorded monthly, so the range covers whole months (inclusive) and sums each agent's total GCE.
        </Typography>
      </Paper>

      {!data ? (
        loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
        ) : (
          <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', py: 4, textAlign: 'center' }}>
            No {incomeLabel.toLowerCase()} data to display.
          </Typography>
        )
      ) : (
        <Stack spacing={2}>
          <Paper elevation={0} sx={cardSx}>
            <Typography variant="subtitle2" sx={sectionTitleSx}>Performer vs non-performer</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)', display: 'block', mb: 1.5 }}>
              {summary?.agents.toLocaleString()} agents in this segment · median {formatSgd(summary?.medianIncome ?? 0)} ·
              average {formatSgd(summary?.averageIncome ?? 0)} · {summary?.zeroIncome.toLocaleString()} with no earnings
            </Typography>
            <CpdPerformerSplitBar
              performers={summary?.performers ?? 0}
              nonPerformers={summary?.nonPerformers ?? 0}
              benchmark={data.benchmark}
              incomeNoun={incomeLabel.toLowerCase()}
              periodLabel={
                fixedYearToDate
                  ? `from ${data.range.startMonth} to ${data.range.endMonth}`
                  : 'over the selected months'
              }
              onSegmentClick={(group) =>
                setDrill({
                  title: group === 'performer' ? 'Performers' : 'Non-performers',
                  group,
                })
              }
            />
          </Paper>

          <Paper ref={incomeCardRef} elevation={0} sx={cardSx}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1,
                mb: 1,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={sectionTitleSx}>Income distribution</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)', display: 'block' }}>
                  Agents grouped by total {incomeLabel.toLowerCase()} from {data.range.startMonth} to {data.range.endMonth}
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
                <Button
                  data-html2canvas-ignore="true"
                  size="small"
                  variant="text"
                  onClick={handleExportCsv}
                  disabled={exporting || !summary?.agents}
                  startIcon={
                    exporting ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />
                  }
                  sx={cardActionSx}
                >
                  Export All
                </Button>
                <CpdDownloadImageButton targetRef={incomeCardRef} fileName="cpd-income-distribution" />
              </Stack>
            </Box>
            <CpdIncomeBandChart
              bands={data.bands}
              benchmark={data.benchmark}
              onBandClick={(band) => setDrill({ title: `${incomeLabel} ${band.label}`, band: band.key })}
            />
          </Paper>
        </Stack>
      )}

      <CpdPerformanceUserListDialog
        open={drill !== null}
        subtitle={subtitle}
        filter={filter}
        drill={drill}
        startMonth={applied.startMonth}
        endMonth={applied.endMonth}
        benchmark={applied.benchmark}
        incomeLabel={incomeLabel}
        showIncome={showIncome}
        allowTemplateEdit={allowTemplateEdit}
        onClose={() => setDrill(null)}
      />
    </Box>
  );
};

/** Standalone wrapper so outstanding-hours list drill-downs can reach the panel. */
export const CpdPerformanceDialog: React.FC<{
  open: boolean;
  title: string;
  subtitle?: string;
  /** Hide the explanatory line beneath the dialog title. */
  hideHeaderSubtitle?: boolean;
  filter: UserListFilter | null;
  /** See CpdPerformancePanel — locks the range to year-to-date and the benchmark to $10,000. */
  fixedYearToDate?: boolean;
  incomeLabel?: string;
  showIncome?: boolean;
  allowTemplateEdit?: boolean;
  onClose: () => void;
}> = ({
  open,
  title,
  subtitle,
  hideHeaderSubtitle = false,
  filter,
  fixedYearToDate = false,
  incomeLabel,
  showIncome,
  allowTemplateEdit,
  onClose,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="md"
    fullWidth
    PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)' } }}
  >
    <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6">{title}</Typography>
          {!hideHeaderSubtitle && (
            <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.72)' }}>
              {subtitle ? `${subtitle} · ` : ''}are these agents performing?
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </Box>
    </DialogTitle>
    <DialogContent sx={{ pt: 2 }}>
      <CpdPerformancePanel
        open={open}
        subtitle={subtitle}
        filter={filter}
        fixedYearToDate={fixedYearToDate}
        incomeLabel={incomeLabel}
        showIncome={showIncome}
        allowTemplateEdit={allowTemplateEdit}
      />
    </DialogContent>
  </Dialog>
);

// ---- Histogram drill-down dialog (from stacked-bar segments) ---------------

const CpdHistogramDialog: React.FC<{
  open: boolean;
  title: string;
  subtitle?: string;
  filter: UserListFilter | null;
  onClose: () => void;
}> = ({ open, title, subtitle, filter, onClose }) => {
  const [tab, setTab] = useState(0);
  const [bucket, setBucket] = useState<number | null>(null);
  const [histogram, setHistogram] = useState<HistogramDatum[]>([]);
  const [agentCount, setAgentCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Commission drill-down for the hours bucket opened from the histogram tab.
  const [bucketPerformanceOpen, setBucketPerformanceOpen] = useState(false);

  const agentsFilter = useMemo<UserListFilter | null>(() => {
    if (!filter) return null;
    return { ...filter, anyOutstanding: true };
  }, [filter]);

  useEffect(() => {
    if (!open) {
      setTab(0);
      setBucket(null);
      setHistogram([]);
      setAgentCount(0);
      setError(null);
      setBucketPerformanceOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !filter) return;
    const status = filter.status;
    const center = filter.center;
    const division = filter.division;
    const snapshot = filter.snapshot;
    const team = filter.team;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        if (USE_CPD_MOCK_DATA) {
          const mock = buildMockCpdAnalytics();
          let rows = mock.users;
          if (status) rows = rows.filter((u) => u.status === status);
          if (center) rows = rows.filter((u) => u[center]);
          if (division) rows = rows.filter((u) => matchesDivisionFilter(u, division));
          const withHours = rows.filter((u) => u.totalOutstanding > 0);
          if (!cancelled) {
            setHistogram(buildMockHistogram(withHours));
            setAgentCount(withHours.length);
          }
          return;
        }
        const res = await api.getCpdAnalyticsUsers({
          status,
          center,
          division,
          snapshot,
          team,
        });
        if (!cancelled) {
          setHistogram(res.histogram ?? []);
          setAgentCount(res.total);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load histogram');
          setHistogram([]);
          setAgentCount(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, filter?.status, filter?.center, filter?.division, filter?.snapshot, filter?.team]);

  const bucketFilter = useMemo<UserListFilter | null>(() => {
    if (bucket === null || !filter) return null;
    return { ...filter, hours: bucket };
  }, [bucket, filter]);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)' } }}>
        <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)', pb: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="h6">{title}</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.72)', mb: 1 }}>
                {subtitle ? `${subtitle} · ` : ''}{agentCount} {agentCount === 1 ? 'agent' : 'agents'} with outstanding hours
              </Typography>
              <Tabs
                value={tab}
                onChange={(_, next) => setTab(next)}
                sx={{
                  minHeight: 40,
                  '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 600 },
                }}
              >
                <Tab label="Outstanding hours" />
                <Tab label="Agents" />
                <Tab label="Performance" />
              </Tabs>
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ mt: 0.25 }}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && tab === 0 && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {tab === 0 && (
            loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
            ) : (
              <CpdHistogram data={histogram} onBarClick={setBucket} height={300} />
            )
          )}
          {tab === 1 && (
            <CpdUserListDialog
              embedded
              open={open}
              title={title}
              subtitle={subtitle}
              filter={agentsFilter}
              onClose={onClose}
              allowSendEmail={!!filter?.division}
            />
          )}
          {tab === 2 && <CpdPerformancePanel open={open} subtitle={subtitle} filter={agentsFilter} />}
        </DialogContent>
      </Dialog>
      <CpdUserListDialog
        open={bucket !== null}
        title={bucket === null ? '' : `${bucket} outstanding hour${bucket === 1 ? '' : 's'}`}
        subtitle={subtitle}
        filter={bucketFilter}
        onClose={() => setBucket(null)}
        onOpenPerformance={() => setBucketPerformanceOpen(true)}
      />
      <CpdPerformanceDialog
        open={bucketPerformanceOpen}
        title={bucket === null ? '' : `${bucket} outstanding hour${bucket === 1 ? '' : 's'} — performance`}
        subtitle={subtitle}
        filter={bucketFilter}
        onClose={() => setBucketPerformanceOpen(false)}
      />
    </>
  );
};

// ---- KPI hero row -----------------------------------------------------------

type ComparisonPeriod = 'last' | 'this';

interface StatusBarRow {
  name: string;
  period: ComparisonPeriod;
  passed: number;
  exempted: number;
  ongoing: number;
}

function countsToBarRow(name: string, period: ComparisonPeriod, counts: CpdStatusCounts): StatusBarRow {
  return {
    name,
    period,
    passed: counts.passed,
    exempted: counts.exempted,
    ongoing: counts.ongoing,
  };
}

/** Week-over-week pill; `positiveIsGood: false` flips the colouring (e.g. fewer Ongoing = good). */
const DeltaChip: React.FC<{
  delta: number | null;
  positiveIsGood?: boolean;
}> = ({ delta, positiveIsGood = true }) => {
  const theme = useChartTheme();
  if (delta === null) return null;
  const up = delta > 0;
  const flat = delta === 0;
  const color = flat
    ? 'rgba(var(--color-text-rgb), 0.6)'
    : up === positiveIsGood
      ? theme.status.Passed
      : theme.status.Ongoing;
  const bg = flat ? 'rgba(var(--color-text-rgb), 0.08)' : hexToRgba(color, 0.15);
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.25,
        px: 0.75,
        py: 0.1,
        borderRadius: 99,
        bgcolor: bg,
        color,
        fontWeight: 700,
        fontSize: '0.72rem',
        lineHeight: 1.7,
        whiteSpace: 'nowrap',
      }}
    >
      {flat ? '—' : up ? <ArrowUpwardIcon sx={{ fontSize: 12 }} /> : <ArrowDownwardIcon sx={{ fontSize: 12 }} />}
      {flat ? 'no change' : `${Math.abs(delta).toLocaleString()} wk/wk`}
    </Box>
  );
};

const KpiTile: React.FC<{
  label: string;
  accent: string;
  value: number;
  share?: number | null;
  delta: number | null;
  positiveIsGood?: boolean;
  /** Makes the tile a button; pair it with `hint` so the affordance is visible. */
  onClick?: () => void;
  /** Small line under the value telling the reader what tapping does. */
  hint?: string;
}> = ({
  label,
  accent,
  value,
  share = null,
  delta,
  positiveIsGood = true,
  onClick,
  hint,
}) => (
  <Box
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={
      onClick
        ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick();
            }
          }
        : undefined
    }
    sx={{
      p: 1.75,
      borderRadius: 2,
      // The accent carries the status, so it owns the border and a solid left rail
      // rather than only tinting the fill — a faint wash reads as decoration and
      // disappears in a screenshot.
      border: `1px solid ${hexToRgba(accent, 0.55)}`,
      borderLeft: `4px solid ${accent}`,
      background: `linear-gradient(150deg, ${hexToRgba(accent, 0.24)}, ${hexToRgba(accent, 0.07)} 70%)`,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
      ...(onClick
        ? {
            cursor: 'pointer',
            transition: 'background 120ms ease, box-shadow 120ms ease',
            '&:hover, &:focus-visible': {
              background: `linear-gradient(150deg, ${hexToRgba(accent, 0.36)}, ${hexToRgba(accent, 0.14)} 70%)`,
              boxShadow: `0 0 0 2px ${hexToRgba(accent, 0.45)}`,
            },
            '&:focus-visible': { outline: 'none' },
          }
        : {}),
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: accent, flexShrink: 0 }} />
      <Typography
        variant="caption"
        sx={{
          fontWeight: 800,
          letterSpacing: 0.6,
          textTransform: 'uppercase',
          color: accent,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
    </Box>
    <Typography
      sx={{
        fontSize: '1.85rem',
        fontWeight: 800,
        lineHeight: 1.1,
        color: 'var(--color-text-strong)',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value.toLocaleString()}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
      {share !== null && (
        <Typography
          variant="caption"
          sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', fontVariantNumeric: 'tabular-nums' }}
        >
          {share.toFixed(1)}% of agents
        </Typography>
      )}
      <DeltaChip delta={delta} positiveIsGood={positiveIsGood} />
    </Box>
    {hint && (
      <Typography
        variant="caption"
        sx={{
          mt: 'auto',
          pt: 0.5,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.4,
          fontWeight: 700,
          color: accent,
        }}
      >
        <TouchAppIcon sx={{ fontSize: 14 }} />
        {hint}
      </Typography>
    )}
  </Box>
);

const StatusDonut: React.FC<{
  counts: CpdStatusCounts;
  size?: number;
}> = ({ counts, size = 118 }) => {
  const theme = useChartTheme();
  const total = statusTotal(counts);
  const strokeWidth = 12;
  const r = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * r;
  const gap = total > 0 ? Math.min(3, circumference / 60) : 0;
  const segments = STACK_ORDER
    .map((status) => ({ status, value: counts[STATUS_KEY[status]] }))
    .filter((s) => s.value > 0);
  // Center % treats exempted as cleared (Passed + Exempted); ring still shows segments separately.
  const passedPct = total > 0 ? ((counts.passed + counts.exempted) / total) * 100 : 0;
  let offset = 0;
  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="rgba(var(--color-border-rgb), 0.12)"
          strokeWidth={strokeWidth}
        />
        {segments.map((seg) => {
          const frac = seg.value / total;
          const dash = Math.max(frac * circumference - gap, 0.5);
          const el = (
            <circle
              key={seg.status}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={theme.status[seg.status]}
              strokeWidth={strokeWidth}
              strokeDasharray={`${dash} ${circumference - dash}`}
              strokeDashoffset={-offset}
            />
          );
          offset += frac * circumference;
          return el;
        })}
      </svg>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: '1.35rem',
            lineHeight: 1,
            color: 'var(--color-text-strong)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {passedPct.toFixed(1)}%
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', fontWeight: 600 }}>
          Passed
        </Typography>
      </Box>
    </Box>
  );
};

export const CpdKpiRow: React.FC<{
  current: CpdStatusCounts;
  lastWeek: CpdStatusCounts;
  hasLastWeekSnapshot: boolean;
  /** Makes the Ongoing tile the drill-in — Team CPD's only way into the agents. */
  onOngoingClick?: () => void;
  /** Tap hint shown on the Ongoing tile; only used alongside `onOngoingClick`. */
  ongoingHint?: string;
}> = ({ current, lastWeek, hasLastWeekSnapshot, onOngoingClick, ongoingHint }) => {
  const theme = useChartTheme();
  const total = statusTotal(current);
  const delta = (curr: number, prev: number) => (hasLastWeekSnapshot ? curr - prev : null);
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)', md: 'auto repeat(4, 1fr)' },
        gap: 1.5,
        alignItems: 'stretch',
      }}
    >
      <Box
        sx={{
          gridColumn: { xs: '1 / -1', sm: '1 / -1', md: 'auto' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          py: 1.5,
          borderRadius: 2,
          border: '1px solid rgba(var(--color-border-rgb), 0.14)',
        }}
      >
        <StatusDonut counts={current} />
      </Box>
      <KpiTile
        label="Total agents"
        accent={theme.accentBlue}
        value={total}
        delta={null}
      />
      <KpiTile
        label="Exempted"
        accent={theme.status.Exempted}
        value={current.exempted}
        delta={null}
      />
      <KpiTile
        label="Passed"
        accent={theme.status.Passed}
        value={current.passed}
        delta={delta(current.passed, lastWeek.passed)}
      />
      <KpiTile
        label="Ongoing"
        accent={theme.status.Ongoing}
        value={current.ongoing}
        delta={null}
        positiveIsGood={false}
        onClick={onOngoingClick && current.ongoing > 0 ? onOngoingClick : undefined}
        hint={onOngoingClick && current.ongoing > 0 ? ongoingHint : undefined}
      />
    </Box>
  );
};

// ---- Status share bands (chart 1) -------------------------------------------

/**
 * Share inside a band segment when it fits; last-week bands stay clean
 * (tooltip + table carry them). Exempted counts as cleared, so its share is
 * folded into the Passed label (Passed + Exempted) and the Exempted segment
 * carries no label of its own — that leaves two complementary percentages on
 * the bar (e.g. 47.4% cleared vs 52.6% ongoing). Exact counts live in the
 * tooltip and the table beside the chart.
 */
function makeBandSegmentLabel(rows: StatusBarRow[], status: CpdStatusBucket, ink: string) {
  return (props: {
    x?: string | number;
    y?: string | number;
    width?: string | number;
    height?: string | number;
    index?: number;
  }) => {
    const x = Number(props.x ?? 0);
    const y = Number(props.y ?? 0);
    const width = Number(props.width ?? 0);
    const height = Number(props.height ?? 0);
    const row = props.index === undefined ? undefined : rows[props.index];
    if (!row || row.period === 'last' || width < 36 || height < 14) return null;
    if (status === 'Exempted') return null;
    const value = row[STATUS_KEY[status]];
    if (value <= 0) return null;
    const total = row.passed + row.exempted + row.ongoing;
    const pctValue = status === 'Passed' ? row.passed + row.exempted : value;
    const pct = total > 0 ? (pctValue / total) * 100 : 0;
    const text = `${pct.toFixed(1)}%`;
    return (
      <text
        x={x + width / 2}
        y={y + height / 2}
        fill={ink}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={11}
        fontWeight={700}
        pointerEvents="none"
      >
        {text}
      </text>
    );
  };
}

const StatusComparisonChart: React.FC<{
  current: CpdStatusCounts;
  lastWeek: CpdStatusCounts;
  hasLastWeekSnapshot: boolean;
  onOngoingClick?: (period: ComparisonPeriod) => void;
  height?: number;
}> = ({ current, lastWeek, hasLastWeekSnapshot, onOngoingClick, height = 210 }) => {
  const theme = useChartTheme();
  const data: StatusBarRow[] = [
    countsToBarRow(hasLastWeekSnapshot ? 'Last week' : 'Last week (no snapshot)', 'last', lastWeek),
    countsToBarRow('This week', 'this', current),
  ];

  const renderTooltip = useCallback(
    (props: unknown) => {
      const { active, payload, label } = props as RechartsTooltipContentProps;
      if (!active || !payload?.length) return null;
      const row = payload[0]?.payload as StatusBarRow | undefined;
      if (!row) return null;
      const total = row.passed + row.exempted + row.ongoing;
      return (
        <ChartTooltipBox
          isDark={theme.isDark}
          title={String(label ?? '')}
          rows={[
            ...STACK_ORDER.map((status) => ({
              color: theme.status[status],
              name: status,
              value: formatCountWithPct(row[STATUS_KEY[status]], total),
            })),
            {
              name: 'Passed + Exempted',
              value: formatCountWithPct(row.passed + row.exempted, total),
            },
            { name: 'Total', value: total.toLocaleString() },
          ]}
          footer={onOngoingClick ? 'Click the Ongoing segment for outstanding hours, agents and commission performance' : undefined}
        />
      );
    },
    [theme, onOngoingClick],
  );

  return (
    <Box>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 4, right: 24, left: 0, bottom: 0 }}
          barCategoryGap="26%"
        >
          <CartesianGrid stroke={theme.grid} strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" allowDecimals={false} stroke={theme.axis} tick={theme.tick} tickLine={false} />
          <YAxis
            type="category"
            dataKey="name"
            width={94}
            stroke={theme.axis}
            tick={theme.tick}
            axisLine={false}
            tickLine={false}
          />
          <RechartsTooltip cursor={{ fill: theme.hoverCursorFill }} content={renderTooltip} />
          <Legend
            content={
              <ChartLegendContent
                items={STACK_ORDER.map((status) => ({ label: status, color: theme.status[status] }))}
              />
            }
          />
          {STACK_ORDER.map((status) => {
            const clickable = status === 'Ongoing' && !!onOngoingClick;
            return (
              <Bar
                key={status}
                dataKey={STATUS_KEY[status]}
                name={status}
                stackId="s"
                fill={theme.status[status]}
                stroke={theme.surface}
                strokeWidth={2}
                maxBarSize={42}
                cursor={clickable ? 'pointer' : undefined}
                onClick={
                  clickable
                    ? (e: { payload?: StatusBarRow }) => {
                        if (!e?.payload) return;
                        if (e.payload.period === 'last' && !hasLastWeekSnapshot) return;
                        onOngoingClick?.(e.payload.period);
                      }
                    : undefined
                }
              >
                {data.map((entry) => (
                  <Cell
                    key={`${status}-${entry.period}`}
                    fill={theme.status[status]}
                    fillOpacity={entry.period === 'last' ? 0.6 : 1}
                  />
                ))}
                <LabelList
                  dataKey={STATUS_KEY[status]}
                  content={makeBandSegmentLabel(data, status, theme.onStatusInk)}
                />
              </Bar>
            );
          })}
        </BarChart>
      </ResponsiveContainer>
      <Typography variant="caption" sx={chartHintSx}>
        Passed % includes exempted · last week shown faded for comparison · hover for exact counts
      </Typography>
    </Box>
  );
};

function statusTotal(counts: CpdStatusCounts): number {
  return counts.passed + counts.exempted + counts.ongoing;
}

function formatCountWithPct(value: number, total: number): string {
  if (total <= 0) return `${value} (0%)`;
  const pct = (value / total) * 100;
  return `${value} (${pct.toFixed(1)}%)`;
}

/** Inline week-over-week delta shown under table cell values; polarity-aware like DeltaChip. */
const CellDelta: React.FC<{ delta: number; positiveIsGood?: boolean }> = ({ delta, positiveIsGood = true }) => {
  const theme = useChartTheme();
  if (delta === 0) return null;
  const up = delta > 0;
  const color = up === positiveIsGood ? theme.status.Passed : theme.status.Ongoing;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 0.15,
        color,
        fontWeight: 600,
        fontSize: '0.75rem',
        lineHeight: 1.2,
      }}
    >
      (
      {up ? <ArrowUpwardIcon sx={{ fontSize: 12 }} /> : <ArrowDownwardIcon sx={{ fontSize: 12 }} />}
      {Math.abs(delta)}
      )
    </Box>
  );
};

const ValueWithDelta: React.FC<{
  children: React.ReactNode;
  delta: number;
  showDelta?: boolean;
  positiveIsGood?: boolean;
}> = ({ children, delta, showDelta = true, positiveIsGood = true }) => (
  <Box
    sx={{
      display: 'inline-flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: 0.15,
    }}
  >
    <Box component="span">{children}</Box>
    {showDelta ? <CellDelta delta={delta} positiveIsGood={positiveIsGood} /> : null}
  </Box>
);

const overviewTableHeadSx = {
  backgroundColor: 'var(--color-surface-thead)',
  color: 'var(--color-text-strong)',
  fontWeight: 600,
  whiteSpace: 'nowrap' as const,
  py: 1,
  px: 1.25,
};

const overviewTableCellSx = {
  whiteSpace: 'nowrap' as const,
  py: 1,
  px: 1.25,
  fontSize: '0.8125rem',
};

/** Small colored square marking a status row label. */
const StatusRowDot: React.FC<{ color?: string }> = ({ color }) =>
  color ? (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        width: 9,
        height: 9,
        borderRadius: '3px',
        bgcolor: color,
        mr: 0.75,
        verticalAlign: 'baseline',
      }}
    />
  ) : null;

const OverviewStatusTable: React.FC<{
  current: CpdStatusCounts;
  lastWeek: CpdStatusCounts;
  hasLastWeekSnapshot: boolean;
}> = ({ current, lastWeek, hasLastWeekSnapshot }) => {
  const theme = useChartTheme();
  const total = statusTotal(current);
  const prevTotal = statusTotal(lastWeek);
  const rows: Array<{
    label: string;
    value: number;
    prev: number;
    bold?: boolean;
    dot?: string;
    positiveIsGood?: boolean;
  }> = [
    { label: 'Exempted', value: current.exempted, prev: lastWeek.exempted, dot: theme.status.Exempted },
    { label: 'Passed', value: current.passed, prev: lastWeek.passed, dot: theme.status.Passed },
    { label: 'Ongoing', value: current.ongoing, prev: lastWeek.ongoing, dot: theme.status.Ongoing, positiveIsGood: false },
    { label: 'Total', value: total, prev: prevTotal, bold: true },
  ];

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={overviewTableHeadSx} />
            <TableCell align="right" sx={overviewTableHeadSx}>This week</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.label}>
              <TableCell sx={{ ...overviewTableCellSx, fontWeight: row.bold ? 700 : 600 }}>
                <StatusRowDot color={row.dot} />
                {row.label}
              </TableCell>
              <TableCell
                align="right"
                sx={{ ...overviewTableCellSx, fontWeight: row.bold ? 700 : 400 }}
              >
                <ValueWithDelta
                  delta={row.value - row.prev}
                  showDelta={hasLastWeekSnapshot && row.label === 'Passed'}
                  positiveIsGood={row.positiveIsGood ?? true}
                >
                  {row.label === 'Total'
                    ? row.value
                    : formatCountWithPct(row.value, total)}
                </ValueWithDelta>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// ---- Training-centre grouped stacked bars (chart 2) ------------------------

const TRAINING_CENTERS: TrainingCenter[] = ['Hastors', 'Huttons', 'Others'];

interface TrainingGroupedRow {
  center: TrainingCenter;
  lastPassed: number;
  lastOngoing: number;
  thisPassed: number;
  thisOngoing: number;
  lastTotal: number;
  thisTotal: number;
  lastPctLabel: string;
  thisPctLabel: string;
}

const TrainingCenterGroupedChart: React.FC<{
  current: CpdAnalyticsResponse['trainingCenters'];
  lastWeek: CpdAnalyticsResponse['trainingCenters'];
  onOngoingClick?: (center: TrainingCenter, period: ComparisonPeriod) => void;
  height?: number;
}> = ({ current, lastWeek, onOngoingClick, height = 300 }) => {
  const theme = useChartTheme();
  const [showLastWeek, setShowLastWeek] = useState(false);

  const thisGrandTotal = TRAINING_CENTERS.reduce((sum, center) => {
    const key = center.toLowerCase() as 'hastors' | 'huttons' | 'others';
    return sum + current[key].passed + current[key].ongoing;
  }, 0);
  const lastGrandTotal = TRAINING_CENTERS.reduce((sum, center) => {
    const key = center.toLowerCase() as 'hastors' | 'huttons' | 'others';
    return sum + lastWeek[key].passed + lastWeek[key].ongoing;
  }, 0);

  const data: TrainingGroupedRow[] = TRAINING_CENTERS.map((center) => {
    const key = center.toLowerCase() as 'hastors' | 'huttons' | 'others';
    const thisTotal = current[key].passed + current[key].ongoing;
    const lastTotal = lastWeek[key].passed + lastWeek[key].ongoing;
    const thisPct = thisGrandTotal > 0 ? (thisTotal / thisGrandTotal) * 100 : 0;
    const lastPct = lastGrandTotal > 0 ? (lastTotal / lastGrandTotal) * 100 : 0;
    return {
      center,
      lastPassed: lastWeek[key].passed,
      lastOngoing: lastWeek[key].ongoing,
      thisPassed: current[key].passed,
      thisOngoing: current[key].ongoing,
      lastTotal,
      thisTotal,
      lastPctLabel: lastTotal > 0 ? `${lastPct.toFixed(1)}%` : '',
      thisPctLabel: thisTotal > 0 ? `${thisPct.toFixed(1)}%` : '',
    };
  });

  const renderTooltip = useCallback(
    (props: unknown) => {
      const { active, payload } = props as RechartsTooltipContentProps;
      if (!active || !payload?.length) return null;
      const row = payload[0]?.payload as TrainingGroupedRow | undefined;
      if (!row) return null;
      const withLastWeek = (thisValue: number, lastValue: number) =>
        showLastWeek
          ? `${thisValue.toLocaleString()} · last wk ${lastValue.toLocaleString()}`
          : thisValue.toLocaleString();
      return (
        <ChartTooltipBox
          isDark={theme.isDark}
          title={row.center}
          rows={[
            { color: theme.status.Passed, name: 'Passed', value: withLastWeek(row.thisPassed, row.lastPassed) },
            { color: theme.status.Ongoing, name: 'Ongoing', value: withLastWeek(row.thisOngoing, row.lastOngoing) },
            { name: 'Total', value: withLastWeek(row.thisTotal, row.lastTotal) },
          ]}
          footer={onOngoingClick ? 'Click the Ongoing segment for outstanding hours, agents and commission performance' : undefined}
        />
      );
    },
    [theme, showLastWeek, onOngoingClick],
  );

  return (
    <Box>
      <Box
        data-html2canvas-ignore="true"
        sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}
      >
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showLastWeek}
              onChange={(_, checked) => setShowLastWeek(checked)}
            />
          }
          label="Show last week"
          sx={{
            mr: 0,
            '& .MuiFormControlLabel-label': {
              fontSize: '0.8125rem',
              color: 'rgba(var(--color-text-rgb), 0.72)',
            },
          }}
        />
      </Box>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 24, right: 12, left: 4, bottom: 4 }} barCategoryGap="24%" barGap={3}>
          <CartesianGrid stroke={theme.grid} strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="center" stroke={theme.axis} tick={theme.tick} tickLine={false} />
          <YAxis allowDecimals={false} stroke={theme.axis} tick={theme.tick} width={40} />
          <RechartsTooltip cursor={{ fill: theme.hoverCursorFill }} content={renderTooltip} />
          {showLastWeek && (
            <>
              {/* Last week stack (left bar in each group) — slightly faded */}
              <Bar
                dataKey="lastPassed"
                name="Passed"
                stackId="last"
                fill={theme.status.Passed}
                fillOpacity={0.55}
                stroke={theme.surface}
                strokeWidth={1.5}
                maxBarSize={44}
                legendType="none"
              />
              <Bar
                dataKey="lastOngoing"
                name="Ongoing"
                stackId="last"
                fill={theme.status.Ongoing}
                fillOpacity={0.55}
                stroke={theme.surface}
                strokeWidth={1.5}
                radius={[5, 5, 0, 0]}
                maxBarSize={44}
                legendType="none"
                cursor={onOngoingClick ? 'pointer' : undefined}
                onClick={
                  onOngoingClick
                    ? (e: { payload?: TrainingGroupedRow }) => {
                        if (e?.payload) onOngoingClick(e.payload.center, 'last');
                      }
                    : undefined
                }
              >
                <LabelList
                  dataKey="lastPctLabel"
                  position="top"
                  style={{ fill: theme.tick.fill, fontSize: 10, fontWeight: 600, opacity: 0.75 }}
                />
              </Bar>
            </>
          )}
          {/* This week stack (right bar in each group) */}
          <Bar
            dataKey="thisPassed"
            name="Passed"
            stackId="this"
            fill={theme.status.Passed}
            stroke={theme.surface}
            strokeWidth={1.5}
            maxBarSize={44}
          />
          <Bar
            dataKey="thisOngoing"
            name="Ongoing"
            stackId="this"
            fill={theme.status.Ongoing}
            stroke={theme.surface}
            strokeWidth={1.5}
            radius={[5, 5, 0, 0]}
            maxBarSize={44}
            cursor={onOngoingClick ? 'pointer' : undefined}
            onClick={
              onOngoingClick
                ? (e: { payload?: TrainingGroupedRow }) => {
                    if (e?.payload) onOngoingClick(e.payload.center, 'this');
                  }
                : undefined
            }
          >
            <LabelList
              dataKey="thisPctLabel"
              position="top"
              style={{ fill: theme.tick.fill, fontSize: 11, fontWeight: 700 }}
            />
          </Bar>
          <Legend
            content={
              <ChartLegendContent
                items={[
                  { label: 'Passed', color: theme.status.Passed },
                  { label: 'Ongoing', color: theme.status.Ongoing },
                ]}
              />
            }
          />
        </BarChart>
      </ResponsiveContainer>
      <Typography variant="caption" sx={chartHintSx}>
        {showLastWeek
          ? 'Each centre shows Last week (faded) then This week · % is share of agents (excl. exempted)'
          : '% is share of agents (excl. exempted)'}
      </Typography>
    </Box>
  );
};

const TrainingCenterTable: React.FC<{
  current: CpdAnalyticsResponse['trainingCenters'];
  lastWeek: CpdAnalyticsResponse['trainingCenters'];
  hasLastWeekSnapshot: boolean;
}> = ({ current, lastWeek, hasLastWeekSnapshot }) => {
  const theme = useChartTheme();
  const centers = TRAINING_CENTERS.map((center) => {
    const key = center.toLowerCase() as 'hastors' | 'huttons' | 'others';
    return { center, counts: current[key], prev: lastWeek[key] };
  });
  const totals: CpdStatusCounts = {
    ongoing: centers.reduce((s, r) => s + r.counts.ongoing, 0),
    passed: centers.reduce((s, r) => s + r.counts.passed, 0),
    exempted: 0,
  };
  const prevTotals: CpdStatusCounts = {
    ongoing: centers.reduce((s, r) => s + r.prev.ongoing, 0),
    passed: centers.reduce((s, r) => s + r.prev.passed, 0),
    exempted: 0,
  };
  const grandTotal = totals.ongoing + totals.passed;

  const statusRows: Array<{
    label: string;
    getValue: (c: CpdStatusCounts) => number;
    bold?: boolean;
    dot?: string;
    positiveIsGood?: boolean;
  }> = [
    { label: 'Ongoing', getValue: (c) => c.ongoing, dot: theme.status.Ongoing, positiveIsGood: false },
    { label: 'Passed', getValue: (c) => c.passed, dot: theme.status.Passed },
    { label: 'Total', getValue: (c) => c.ongoing + c.passed, bold: true },
  ];

  const cell = (
    key: string,
    value: number,
    prev: number,
    ofTotal: number,
    bold = false,
    positiveIsGood = true,
    showDelta = false,
  ) => (
    <TableCell key={key} align="right" sx={{ ...overviewTableCellSx, fontWeight: bold ? 700 : 400 }}>
      <ValueWithDelta
        delta={value - prev}
        showDelta={hasLastWeekSnapshot && showDelta}
        positiveIsGood={positiveIsGood}
      >
        {formatCountWithPct(value, ofTotal)}
      </ValueWithDelta>
    </TableCell>
  );

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={overviewTableHeadSx} />
            {centers.map((c) => (
              <TableCell key={c.center} align="right" sx={overviewTableHeadSx}>{c.center}</TableCell>
            ))}
            <TableCell align="right" sx={overviewTableHeadSx}>Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {statusRows.map((row) => {
            const rowTotal = row.getValue(totals);
            const rowPrevTotal = row.getValue(prevTotals);
            return (
              <TableRow key={row.label}>
                <TableCell sx={{ ...overviewTableCellSx, fontWeight: row.bold ? 700 : 600 }}>
                  <StatusRowDot color={row.dot} />
                  {row.label}
                </TableCell>
                {centers.map((c) =>
                  cell(
                    c.center,
                    row.getValue(c.counts),
                    row.getValue(c.prev),
                    grandTotal,
                    row.bold,
                    row.positiveIsGood ?? true,
                    row.label === 'Passed',
                  ),
                )}
                {row.label === 'Total'
                  ? (
                    <TableCell align="right" sx={{ ...overviewTableCellSx, fontWeight: 700 }}>
                      {grandTotal}
                    </TableCell>
                  )
                  : cell(
                    'row-total',
                    rowTotal,
                    rowPrevTotal,
                    grandTotal,
                    true,
                    row.positiveIsGood ?? true,
                    row.label === 'Passed',
                  )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// ---- Division fulfilment (%, counts) ---------------------------------------

type DivisionStatusRow = CpdStatusCounts & { division: string };

interface DivisionChartDatum {
  division: string;
  /** Passed + exempted — an exempted agent has nothing left to clear. */
  cleared: number;
  /** Kept apart from `cleared` so the tooltip can break the bar down. */
  passed: number;
  exempted: number;
  ongoing: number;
  clearedPct: number;
  ongoingPct: number;
  clearedPctLabel: string;
  ongoingPctLabel: string;
  clearedLabel: string;
  ongoingLabel: string;
}

function buildDivisionChartData(rows: DivisionStatusRow[]): DivisionChartDatum[] {
  return rows
    .map((row) => {
      // Exempted counts as cleared: those agents carry no outstanding requirement,
      // so leaving them out understated every division's cleared share.
      const cleared = row.passed + row.exempted;
      const ongoing = row.ongoing;
      const total = cleared + ongoing;
      if (total <= 0) return null;
      const clearedPct = Math.round((cleared / total) * 100);
      const ongoingPct = 100 - clearedPct;
      return {
        division: row.division || '-',
        cleared,
        passed: row.passed,
        exempted: row.exempted,
        ongoing,
        clearedPct,
        ongoingPct,
        clearedPctLabel: clearedPct > 0 ? `${clearedPct}%` : '',
        ongoingPctLabel: ongoingPct > 0 ? `${ongoingPct}%` : '',
        clearedLabel: cleared > 0 ? String(cleared) : '',
        ongoingLabel: ongoing > 0 ? String(ongoing) : '',
      };
    })
    .filter((d): d is DivisionChartDatum => d !== null);
}

/** Centered in-segment label for the horizontal division bars; hidden when the segment is too narrow. */
function makeDivisionInsideLabel(ink: string) {
  return (props: {
    x?: string | number;
    y?: string | number;
    width?: string | number;
    height?: string | number;
    value?: string | number;
  }) => {
    const x = Number(props.x ?? 0);
    const y = Number(props.y ?? 0);
    const width = Number(props.width ?? 0);
    const height = Number(props.height ?? 0);
    const value = props.value;
    if (value === undefined || value === null || value === '') return null;
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(width) || !Number.isFinite(height)) {
      return null;
    }
    if (width < 34 || height < 12) return null;
    return (
      <text
        x={x + width / 2}
        y={y + height / 2}
        fill={ink}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={11}
        fontWeight={700}
        pointerEvents="none"
      >
        {value}
      </text>
    );
  };
}

const DIVISION_TICK_MAX_CHARS = 18;

const DivisionAxisTick: React.FC<{
  x?: number;
  y?: number;
  payload?: { value?: string };
  onClick?: (division: string) => void;
}> = ({ x = 0, y = 0, payload, onClick }) => {
  const value = payload?.value || '';
  const shown =
    value.length > DIVISION_TICK_MAX_CHARS ? `${value.slice(0, DIVISION_TICK_MAX_CHARS - 1)}…` : value;
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={-6}
        y={0}
        dy={4}
        textAnchor="end"
        fill="currentColor"
        fontSize={12}
        style={{ cursor: onClick ? 'pointer' : 'default' }}
        onClick={() => {
          if (value && onClick) onClick(value);
        }}
      >
        <title>{value}</title>
        {shown}
      </text>
    </g>
  );
};

type DivisionView = 'pct' | 'count';

const DivisionFulfilmentCharts: React.FC<{
  divisions: DivisionStatusRow[];
  onDivisionClick?: (division: string) => void;
  onOngoingClick?: (division: string) => void;
}> = ({ divisions, onDivisionClick, onOngoingClick }) => {
  const theme = useChartTheme();
  const [view, setView] = useState<DivisionView>('pct');
  const data = useMemo(() => buildDivisionChartData(divisions), [divisions]);

  const renderTooltip = useCallback(
    (props: unknown) => {
      const { active, payload } = props as RechartsTooltipContentProps;
      if (!active || !payload?.length) return null;
      const row = payload[0]?.payload as DivisionChartDatum | undefined;
      if (!row) return null;
      // The green segment is one number on the bar but two statuses underneath, so
      // the tooltip splits it — an exempted agent is cleared for a different reason
      // than a passed one.
      const subRow = (label: string, color: string, value: number): ChartTooltipRow => ({
        name: (
          <Box component="span" sx={{ pl: 2.5, display: 'inline-flex', alignItems: 'center', gap: 0.6 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
            {label}
          </Box>
        ),
        value: value.toLocaleString(),
      });
      return (
        <ChartTooltipBox
          isDark={theme.isDark}
          title={row.division}
          rows={[
            { color: theme.status.Passed, name: 'Cleared', value: `${row.cleared.toLocaleString()} (${row.clearedPct}%)` },
            subRow('Passed', theme.status.Passed, row.passed),
            subRow('Exempted', theme.status.Exempted, row.exempted),
            { color: theme.status.Ongoing, name: 'Ongoing', value: `${row.ongoing.toLocaleString()} (${row.ongoingPct}%)` },
            { name: 'Total', value: (row.cleared + row.ongoing).toLocaleString() },
          ]}
          footer="Click the division name for the agent list · click the Ongoing segment for outstanding hours"
        />
      );
    },
    [theme],
  );

  if (data.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', py: 4, textAlign: 'center' }}>
        No division data available.
      </Typography>
    );
  }

  const chartHeight = Math.max(240, data.length * 36 + 76);
  const isPct = view === 'pct';
  const ongoingClick = onOngoingClick
    ? (e: { payload?: DivisionChartDatum }) => {
        if (e?.payload?.division) onOngoingClick(e.payload.division);
      }
    : undefined;
  const insideLabel = makeDivisionInsideLabel(theme.onStatusInk);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
          mb: 1,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.82)' }}>
          {isPct ? 'Share of agents cleared vs ongoing' : 'Agents cleared vs ongoing'}
        </Typography>
        <ToggleButtonGroup
          data-html2canvas-ignore="true"
          size="small"
          exclusive
          value={view}
          onChange={(_, next: DivisionView | null) => {
            if (next) setView(next);
          }}
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontWeight: 600,
              px: 1.5,
              py: 0.25,
              color: 'rgba(var(--color-text-rgb), 0.7)',
            },
          }}
        >
          <ToggleButton value="pct">% share</ToggleButton>
          <ToggleButton value="count">Head count</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 4, right: 32, left: 8, bottom: 4 }}
          barCategoryGap="26%"
        >
          <CartesianGrid stroke={theme.grid} strokeDasharray="3 3" horizontal={false} />
          <XAxis
            type="number"
            domain={isPct ? [0, 100] : undefined}
            allowDecimals={false}
            stroke={theme.axis}
            tick={theme.tick}
            tickLine={false}
            tickFormatter={isPct ? (v: number) => `${v}%` : undefined}
          />
          <YAxis
            type="category"
            dataKey="division"
            width={132}
            interval={0}
            stroke={theme.axis}
            tick={(props) => <DivisionAxisTick {...props} onClick={onDivisionClick} />}
            axisLine={false}
            tickLine={false}
          />
          <RechartsTooltip cursor={{ fill: theme.hoverCursorFill }} content={renderTooltip} />
          <Legend
            content={
              <ChartLegendContent
                items={[
                  { label: 'Cleared', color: theme.status.Passed },
                  { label: 'Ongoing', color: theme.status.Ongoing },
                ]}
              />
            }
          />
          <Bar
            dataKey={isPct ? 'clearedPct' : 'cleared'}
            name="Cleared"
            stackId="d"
            fill={theme.status.Passed}
            stroke={theme.surface}
            strokeWidth={2}
            maxBarSize={26}
          >
            <LabelList dataKey={isPct ? 'clearedPctLabel' : 'clearedLabel'} content={insideLabel} />
          </Bar>
          <Bar
            dataKey={isPct ? 'ongoingPct' : 'ongoing'}
            name="Ongoing"
            stackId="d"
            fill={theme.status.Ongoing}
            stroke={theme.surface}
            strokeWidth={2}
            maxBarSize={26}
            cursor={onOngoingClick ? 'pointer' : undefined}
            onClick={ongoingClick}
          >
            <LabelList dataKey={isPct ? 'ongoingPctLabel' : 'ongoingLabel'} content={insideLabel} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <Typography variant="caption" sx={chartHintSx}>
        Cleared counts passed and exempted agents · click a division name for its agent list · click a red segment for outstanding hours
      </Typography>
    </Box>
  );
};

// ---- Upload control --------------------------------------------------------

const UploadButton: React.FC<{
  label: string;
  busy: boolean;
  disabled: boolean;
  onFile: (file: File) => void;
}> = ({ label, busy, disabled, onFile }) => {
  const ref = useRef<HTMLInputElement | null>(null);
  return (
    <>
      <input
        ref={ref}
        type="file"
        accept={SPREADSHEET_ACCEPT}
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = '';
        }}
      />
      <Button
        variant="contained"
        size="small"
        disableElevation
        startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <UploadFileIcon />}
        disabled={disabled || busy}
        onClick={() => ref.current?.click()}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.8125rem',
          px: 1.75,
          py: 0.75,
          borderRadius: 1.5,
          bgcolor: 'rgba(var(--color-text-rgb), 0.08)',
          color: 'var(--color-text-strong)',
          border: '1px solid rgba(var(--color-border-rgb), 0.22)',
          boxShadow: 'none',
          '&:hover': {
            bgcolor: 'rgba(var(--color-text-rgb), 0.14)',
            boxShadow: 'none',
          },
          '&.Mui-disabled': {
            bgcolor: 'rgba(var(--color-text-rgb), 0.04)',
            color: 'rgba(var(--color-text-rgb), 0.38)',
            borderColor: 'rgba(var(--color-border-rgb), 0.12)',
          },
        }}
      >
        {busy ? 'Uploading…' : label}
      </Button>
    </>
  );
};

const UploadGroup: React.FC<{
  title: string;
  cadence: string;
  hint: string;
  children: React.ReactNode;
}> = ({ title, cadence, hint, children }) => (
  <Box
    sx={{
      flex: '1 1 280px',
      minWidth: 0,
      p: 1.75,
      borderRadius: 2,
      bgcolor: 'rgba(var(--color-text-rgb), 0.03)',
      border: '1px solid rgba(var(--color-border-rgb), 0.14)',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>
        {title}
      </Typography>
      <Typography
        component="span"
        variant="caption"
        sx={{
          fontWeight: 600,
          letterSpacing: 0.2,
          color: 'rgba(var(--color-text-rgb), 0.55)',
          textTransform: 'uppercase',
          fontSize: '0.65rem',
        }}
      >
        {cadence}
      </Typography>
    </Box>
    <Typography
      variant="caption"
      sx={{ display: 'block', color: 'rgba(var(--color-text-rgb), 0.62)', mb: 1.5, lineHeight: 1.45 }}
    >
      {hint}
    </Typography>
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {children}
    </Stack>
  </Box>
);

// ---- Main tab --------------------------------------------------------------

interface CpdAnalyticsTabProps {
  isAdmin: boolean;
}

const CpdAnalyticsTab: React.FC<CpdAnalyticsTabProps> = ({ isAdmin }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<CpdAnalyticsResponse | null>(null);
  const [uploadingReq, setUploadingReq] = useState(false);
  const [uploadingHastor, setUploadingHastor] = useState(false);
  const [uploadingAttendance, setUploadingAttendance] = useState(false);
  const [uploadMessage, setUploadMessage] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);

  // Drill-downs
  const [segmentDrill, setSegmentDrill] = useState<{
    title: string;
    subtitle?: string;
    filter: UserListFilter;
  } | null>(null);
  const [divisionListDrill, setDivisionListDrill] = useState<{
    title: string;
    subtitle?: string;
    filter: UserListFilter;
  } | null>(null);
  const [histogramDrill, setHistogramDrill] = useState<{
    hours: number;
    hoursField: CpdHoursField;
  } | null>(null);
  /**
   * Commission drill-down opened from a list. Holds its own filter so closing the
   * list underneath does not tear the performance dialog down with it.
   */
  const [performanceDrill, setPerformanceDrill] = useState<{
    title: string;
    subtitle?: string;
    filter: UserListFilter;
  } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (USE_CPD_MOCK_DATA) {
        setData(buildMockCpdAnalytics());
        return;
      }
      const res = await api.getCpdAnalytics();
      setData(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load CPD analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const mainBucketFilter = useMemo<UserListFilter | null>(
    () =>
      histogramDrill === null
        ? null
        : { hours: histogramDrill.hours, hoursField: histogramDrill.hoursField },
    [histogramDrill],
  );

  const openHistogramBucket = useCallback((hours: number, hoursField: CpdHoursField) => {
    setHistogramDrill({ hours, hoursField });
  }, []);

  const handleUploadRequirements = useCallback(async (file: File) => {
    setUploadingReq(true);
    setUploadMessage(null);
    setError(null);
    try {
      const rows = await parseSpreadsheetRows(file);
      if (rows.length < 2) throw new Error('File has no data rows');
      const find = headerIndex(rows[0]);
      const iCea = find(['CEA Registration Number', 'Agent CEA', 'CEA']);
      const iCycle = find(['CPD Cycle']);
      const iStatus = find(['CPD Fulfilment Status', 'CPD Status']);
      const iReqPE = find(['Required PE Hours']);
      const iReqSL = find(['Required SL Hours']);
      const iReqSDL = find(['Required SDL Hours']);
      const iCompPE = find(['Completed SL (PE) Hours', 'Completed PE Hours']);
      const iCompSL = find(['Completed SL (PC) Hours', 'Completed SL Hours']);
      const iCompSDL = find(['Completed SDL Hours']);
      const iOutPE = find(['Outstanding SL (PE) Hours', 'Outstanding PE Hours']);
      const iOutSL = find(['Outstanding SL (PC) Hours', 'Outstanding SL Hours']);
      const iOutSDL = find(['Outstanding SDL Hours']);
      if (iCea === -1) throw new Error('Could not find "CEA Registration Number" column');

      const payload = rows.slice(1)
        .map((r) => ({
          ceaNumber: (r[iCea] || '').trim(),
          cpdCycle: iCycle === -1 ? '' : (r[iCycle] || '').trim(),
          cpdStatus: iStatus === -1 ? '' : (r[iStatus] || '').trim(),
          requiredPE: toIntCsv(r[iReqPE]),
          requiredSL: toIntCsv(r[iReqSL]),
          requiredSDL: toIntCsv(r[iReqSDL]),
          completedPE: toIntCsv(r[iCompPE]),
          completedSL: toIntCsv(r[iCompSL]),
          completedSDL: toIntCsv(r[iCompSDL]),
          outstandingPE: toIntCsv(r[iOutPE]),
          outstandingSL: toIntCsv(r[iOutSL]),
          outstandingSDL: toIntCsv(r[iOutSDL]),
        }))
        .filter((r) => r.ceaNumber);
      if (payload.length === 0) throw new Error('No valid rows found');

      const result = await uploadRowsInBatches(
        payload,
        (batch, options) => api.uploadCpdRequirements(batch, options),
        (part, totalParts) => setUploadProgress(`Uploading batch ${part} of ${totalParts}…`),
      );
      setUploadMessage(`Requirements: imported ${result.imported}${result.skipped ? `, skipped ${result.skipped}` : ''}.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload requirements file');
    } finally {
      setUploadingReq(false);
      setUploadProgress(null);
    }
  }, [load]);

  const handleUploadHastor = useCallback(async (file: File) => {
    setUploadingHastor(true);
    setUploadMessage(null);
    setError(null);
    try {
      const rows = await parseSpreadsheetRows(file);
      if (rows.length < 2) throw new Error('File has no data rows');
      const find = headerIndex(rows[0]);
      const iCea = find(['Agent CEA', 'Agent CE', 'CEA Registration Number', 'CEA']);
      const iPlan = find(['Package Plan', 'Package P']);
      const iDate = find(['Purchase Date/Time', 'Purchase Date / Time', 'Purchase Date Time', 'Purchase Date']);
      if (iCea === -1) {
        throw new Error('Could not find Agent CEA column (looked for "Agent CEA" / "Agent CE")');
      }
      if (iDate === -1) {
        throw new Error('Could not find Purchase Date/Time column');
      }

      let invalidDates = 0;
      const payload = rows.slice(1)
        .map((r) => {
          const rawDate = (r[iDate] || '').trim();
          const purchaseDateTime = parsePurchaseDateTime(rawDate);
          if (rawDate && !purchaseDateTime) invalidDates += 1;
          return {
            ceaNumber: (r[iCea] || '').trim(),
            packagePlan: iPlan === -1 ? '' : (r[iPlan] || '').trim(),
            purchaseDateTime: purchaseDateTime || '',
          };
        })
        .filter((r) => r.ceaNumber && r.purchaseDateTime);
      if (payload.length === 0) {
        throw new Error(
          invalidDates > 0
            ? `No valid rows found (${invalidDates} row(s) had unrecognised purchase dates — expected DD/MM/YYYY h:mm AM/PM)`
            : 'No valid rows found (check Agent CEA and Purchase Date/Time columns)',
        );
      }

      const result = await uploadRowsInBatches(
        payload,
        (batch, options) => api.uploadCpdHastor(batch, options),
        (part, totalParts) => setUploadProgress(`Uploading batch ${part} of ${totalParts}…`),
      );
      setUploadMessage(`Hastor trainings: imported ${result.imported}${result.skipped ? `, skipped ${result.skipped}` : ''}.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload hastor file');
    } finally {
      setUploadingHastor(false);
      setUploadProgress(null);
    }
  }, [load]);

  const handleUploadAttendance = useCallback(async (file: File) => {
    setUploadingAttendance(true);
    setUploadMessage(null);
    setError(null);
    try {
      const attendedDate = parseAttendedDateFromFilename(file.name);
      if (!attendedDate) {
        throw new Error(
          'Could not derive attended date from filename. Expected a name containing CPD-yyyymmdd (e.g. CPD-20260315.xlsx).',
        );
      }

      const rows = await parseSpreadsheetRows(file);
      if (rows.length < 2) throw new Error('File has no data rows');
      const find = headerIndex(rows[0]);
      const iCea = find(['CEA NO', 'CEA No', 'CEA Number']);
      const iNric = find(['RES NRIC/FIN', 'RES NRIC / FIN', 'RES ID', 'NRIC/FIN', 'NRIC']);
      if (iCea === -1 && iNric === -1) {
        throw new Error(
          'Could not find identity column. Expected header "CEA NO", "RES NRIC/FIN", or "RES ID".',
        );
      }

      const useCea = iCea !== -1;
      const payload = rows
        .slice(1)
        .map((r) => {
          if (useCea) {
            const ceaNumber = (r[iCea] || '').trim();
            return ceaNumber ? { ceaNumber, attendedDate } : null;
          }
          const nric = (r[iNric] || '').trim();
          return nric ? { nric, attendedDate } : null;
        })
        .filter((r): r is { ceaNumber: string; attendedDate: string } | { nric: string; attendedDate: string } => r != null);

      if (payload.length === 0) {
        throw new Error(
          useCea
            ? 'No valid rows found (CEA NO column was empty)'
            : 'No valid rows found (RES NRIC/FIN or RES ID column was empty)',
        );
      }

      const result = await uploadRowsInBatches(
        payload,
        (batch, options) => api.uploadCpdAttendance(batch, options),
        (part, totalParts) => setUploadProgress(`Uploading batch ${part} of ${totalParts}…`),
      );
      const skipDetail = Object.entries(result.skipReasons || {})
        .map(([reason, count]) => `${reason}: ${count}`)
        .join('; ');
      setUploadMessage(
        [
          `CPD attendance: imported ${result.imported}`,
          result.skipped ? `skipped ${result.skipped}${skipDetail ? ` (${skipDetail})` : ''}` : null,
          `session ${attendedDate.slice(0, 10)}`,
        ]
          .filter(Boolean)
          .join(', ') + '.',
      );
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload CPD attendance file');
    } finally {
      setUploadingAttendance(false);
      setUploadProgress(null);
    }
  }, [load]);

  const openSegmentDrill = useCallback((period: ComparisonPeriod) => {
    const isLast = period === 'last';
    const weekLabel = isLast ? 'Last week' : 'This week';
    setSegmentDrill({
      title: `${weekLabel} · Ongoing — outstanding hours`,
      subtitle: `${weekLabel} · Ongoing`,
      filter: {
        status: 'Ongoing',
        snapshot: isLast ? 'last' : 'current',
      },
    });
  }, []);

  const openCenterDrill = useCallback((center: TrainingCenter, period: ComparisonPeriod) => {
    const key = center.toLowerCase() as 'hastors' | 'huttons' | 'others';
    const isLast = period === 'last';
    const weekLabel = isLast ? 'Last week' : 'This week';
    setSegmentDrill({
      title: `${weekLabel} · ${center} · Ongoing — outstanding hours`,
      subtitle: `${weekLabel} · ${center} · Ongoing`,
      filter: {
        status: 'Ongoing',
        center: key,
        snapshot: isLast ? 'last' : 'current',
      },
    });
  }, []);

  const openDivisionDrill = useCallback((division: string) => {
    setSegmentDrill({
      title: `${division} · Ongoing — outstanding hours`,
      subtitle: `${division} · Ongoing`,
      filter: {
        status: 'Ongoing',
        division,
        snapshot: 'current',
      },
    });
  }, []);

  const openDivisionListDrill = useCallback((division: string) => {
    setDivisionListDrill({
      title: `${division} · CPD fulfilment`,
      subtitle: division,
      filter: {
        division,
        includeAll: true,
        snapshot: 'current',
      },
    });
  }, []);

  return (
    <Box>
      {isAdmin && (
        <Paper elevation={0} sx={{ ...cardSx, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ ...sectionTitleSx, mb: 0.5 }}>
            Data upload
          </Typography>
          <Typography
            variant="caption"
            sx={{ display: 'block', color: 'rgba(var(--color-text-rgb), 0.62)', mb: 2, lineHeight: 1.45 }}
          >
            Upload CSV or XLSX. Weekly files replace that dataset&apos;s snapshot. Attendance files need{' '}
            <code>CPD-yyyymmdd</code> in the name and a <code>CEA NO</code>, <code>RES NRIC/FIN</code>, or{' '}
            <code>RES ID</code> column.
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="stretch">
            <UploadGroup
              title="Weekly snapshots"
              cadence="Weekly"
              hint="CPD requirements and Hastor trainings — replace the active snapshot each week."
            >
              <UploadButton
                label="CPD requirements"
                busy={uploadingReq}
                disabled={uploadingHastor || uploadingAttendance}
                onFile={handleUploadRequirements}
              />
              <UploadButton
                label="Hastor trainings"
                busy={uploadingHastor}
                disabled={uploadingReq || uploadingAttendance}
                onFile={handleUploadHastor}
              />
            </UploadGroup>
            <UploadGroup
              title="CPD attendance"
              cadence="After each event"
              hint="Upload after a CPD session. NRIC / RES ID is resolved to an Active user's CEA on import. Out-of-year rows are soft-defuncted."
            >
              <UploadButton
                label="CPD Attendance"
                busy={uploadingAttendance}
                disabled={uploadingReq || uploadingHastor}
                onFile={handleUploadAttendance}
              />
            </UploadGroup>
          </Stack>
          {uploadProgress && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'rgba(var(--color-text-rgb), 0.65)' }}>
              {uploadProgress}
            </Typography>
          )}
          {uploadMessage && (
            <Alert severity="success" sx={{ mt: 1.5 }} onClose={() => setUploadMessage(null)}>
              {uploadMessage}
            </Alert>
          )}
        </Paper>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)' }}>
          {data?.current.lastUploadedAt
            ? `Last updated ${new Date(data.current.lastUploadedAt).toLocaleString()}`
            : 'No data uploaded yet'}
        </Typography>
        <Tooltip title="Refresh">
          <span>
            <IconButton size="small" onClick={load} disabled={loading}><RefreshIcon /></IconButton>
          </span>
        </Tooltip>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : !data ? (
        <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', py: 6, textAlign: 'center' }}>
          No CPD data to display.
        </Typography>
      ) : (
        <Stack spacing={2}>
          {USE_CPD_MOCK_DATA && (
            <Alert severity="info">
              Showing temporary mock data (with last-week vs this-week values). Set <code>USE_CPD_MOCK_DATA</code> to <code>false</code> to use the backend again.
            </Alert>
          )}
          {/* KPI hero: this week at a glance */}
          <CpdSectionCard
            title="This week at a glance"
            subtitle="CPD fulfilment across all agents · pie Passed % includes exempted · week-over-week shown on Passed only"
            fileName="cpd-week-at-a-glance"
          >
            <CpdKpiRow
              current={data.current.statusCounts}
              lastWeek={data.lastWeek.statusCounts}
              hasLastWeekSnapshot={data.lastWeek.hasSnapshot}
            />
          </CpdSectionCard>

          {/* Chart 1: CPD status — last week vs this week */}
          <CpdSectionCard
            title="CPD status overview"
            subtitle="Last week vs this week · Passed % includes exempted · click the red Ongoing segment for outstanding hours, agents and commission performance"
            fileName="cpd-status-overview"
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                flexWrap: 'wrap',
                gap: 2,
                alignItems: { xs: 'flex-start', md: 'center' },
              }}
            >
              <Box sx={{ flex: '1 1 320px', minWidth: 0, width: { xs: '100%', md: 'auto' } }}>
                <StatusComparisonChart
                  current={data.current.statusCounts}
                  lastWeek={data.lastWeek.statusCounts}
                  hasLastWeekSnapshot={data.lastWeek.hasSnapshot}
                  onOngoingClick={openSegmentDrill}
                />
              </Box>
              <Box sx={{ flex: '0 0 auto', width: { xs: '100%', md: 'fit-content' }, maxWidth: '100%', overflowX: 'auto' }}>
                <OverviewStatusTable
                  current={data.current.statusCounts}
                  lastWeek={data.lastWeek.statusCounts}
                  hasLastWeekSnapshot={data.lastWeek.hasSnapshot}
                />
              </Box>
            </Box>
          </CpdSectionCard>

          {/* Chart 2: training-centre breakdown */}
          <CpdSectionCard
            title="Training Center Market Share"
            subtitle="Passed vs ongoing agents per training centre (exempted excluded) · click a red segment for outstanding hours, agents and commission performance"
            fileName="trainings-by-training-centre"
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                flexWrap: 'wrap',
                gap: 2,
                alignItems: 'flex-start',
              }}
            >
              <Box sx={{ flex: '1 1 280px', minWidth: 0, width: { xs: '100%', md: 'auto' } }}>
                <TrainingCenterGroupedChart
                  current={data.trainingCenters}
                  lastWeek={data.lastWeekTrainingCenters ?? {
                    hastors: { passed: 0, exempted: 0, ongoing: 0 },
                    huttons: { passed: 0, exempted: 0, ongoing: 0 },
                    others: { passed: 0, exempted: 0, ongoing: 0 },
                  }}
                  onOngoingClick={openCenterDrill}
                />
              </Box>
              <Box sx={{ flex: '0 0 auto', width: { xs: '100%', md: 'fit-content' }, maxWidth: '100%', overflowX: 'auto' }}>
                <TrainingCenterTable
                  current={data.trainingCenters}
                  lastWeek={data.lastWeekTrainingCenters ?? {
                    hastors: { passed: 0, exempted: 0, ongoing: 0 },
                    huttons: { passed: 0, exempted: 0, ongoing: 0 },
                    others: { passed: 0, exempted: 0, ongoing: 0 },
                  }}
                  hasLastWeekSnapshot={data.lastWeek.hasSnapshot}
                />
              </Box>
            </Box>
          </CpdSectionCard>

          {/* Outstanding-hours breakdown: PE bar + SL / SDL histograms */}
          <CpdSectionCard
            title="Outstanding hours breakdown (PE / SL / SDL)"
            subtitle="Where the outstanding hours sit by requirement type · click any bar to list the agents, then Performance for their commission"
            fileName="outstanding-hours-breakdown-by-type"
          >
            <CpdOutstandingBreakdownSection
              pe={data.histogramPE ?? []}
              sl={data.histogramSL ?? []}
              sdl={data.histogramSDL ?? []}
              onBarClick={(hours, field) => openHistogramBucket(hours, field)}
            />
          </CpdSectionCard>

          <CpdSectionCard
            title="Outstanding CPD hours distribution"
            subtitle="Agents grouped by total outstanding hours — deeper red means more hours outstanding · click a bar to list the agents, then Performance for their commission"
            fileName="outstanding-cpd-hours-distribution"
          >
            <CpdHistogram
              data={data.histogram ?? []}
              onBarClick={(hours) => openHistogramBucket(hours, 'total')}
              height={300}
              xAxisLabel="Outstanding hours (total)"
            />
          </CpdSectionCard>

          <CpdSectionCard
            title="CPD fulfilment by division"
            subtitle="Cleared vs ongoing agents in each division"
            fileName="cpd-fulfilment-by-division"
          >
            <DivisionFulfilmentCharts
              divisions={data.divisions ?? []}
              onDivisionClick={openDivisionListDrill}
              onOngoingClick={openDivisionDrill}
            />
          </CpdSectionCard>
        </Stack>
      )}

      {/* Drill-downs */}
      <CpdHistogramDialog
        open={segmentDrill !== null}
        title={segmentDrill?.title ?? ''}
        subtitle={segmentDrill?.subtitle}
        filter={segmentDrill?.filter ?? null}
        onClose={() => setSegmentDrill(null)}
      />
      <CpdUserListDialog
        open={histogramDrill !== null}
        title={
          histogramDrill === null
            ? ''
            : `${histogramDrill.hours} outstanding ${HOURS_FIELD_LABEL[histogramDrill.hoursField]} hour${histogramDrill.hours === 1 ? '' : 's'}`
        }
        filter={mainBucketFilter}
        onClose={() => setHistogramDrill(null)}
        onOpenPerformance={
          histogramDrill === null || mainBucketFilter === null
            ? undefined
            : () =>
                setPerformanceDrill({
                  title: `${histogramDrill.hours} outstanding ${HOURS_FIELD_LABEL[histogramDrill.hoursField]} hour${histogramDrill.hours === 1 ? '' : 's'} — performance`,
                  filter: mainBucketFilter,
                })
        }
      />
      <CpdUserListDialog
        open={divisionListDrill !== null}
        title={divisionListDrill?.title ?? ''}
        subtitle={divisionListDrill?.subtitle}
        filter={divisionListDrill?.filter ?? null}
        onClose={() => setDivisionListDrill(null)}
        onOpenPerformance={
          divisionListDrill === null
            ? undefined
            : () =>
                setPerformanceDrill({
                  title: `${divisionListDrill.title} — performance`,
                  subtitle: divisionListDrill.subtitle,
                  filter: divisionListDrill.filter,
                })
        }
      />
      <CpdPerformanceDialog
        open={performanceDrill !== null}
        title={performanceDrill?.title ?? ''}
        subtitle={performanceDrill?.subtitle}
        filter={performanceDrill?.filter ?? null}
        onClose={() => setPerformanceDrill(null)}
      />
    </Box>
  );
};

export default CpdAnalyticsTab;
